import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Slider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExploreIcon from '@mui/icons-material/Explore';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PublicIcon from '@mui/icons-material/Public';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import SunCalc from 'suncalc';
import * as satellite from 'satellite.js';
import { config } from '../config';

const OBJECT_TYPES = {
  galaxy: { label: 'Galaxies', color: '#80d7ff', glow: 'rgba(128,215,255,0.35)' },
  nebula: { label: 'Nebulae', color: '#ff77bf', glow: 'rgba(255,119,191,0.36)' },
  cluster: { label: 'Open clusters', color: '#ffd36f', glow: 'rgba(255,211,111,0.32)' },
  globular: { label: 'Globular clusters', color: '#bda1ff', glow: 'rgba(189,161,255,0.34)' },
  planetary: { label: 'Planetary nebulae', color: '#76ffdc', glow: 'rgba(118,255,220,0.32)' }
};

const CATALOGUE_FILTERS = {
  messier: { label: 'Messier', short: 'M', patterns: ['M'], objects: 110, note: 'Classic bright DSOs' },
  ngc: { label: 'New General Catalogue', short: 'NGC', patterns: ['NGC'], objects: 7840, note: 'Primary DSO catalogue' },
  ic: { label: 'Index Catalogue', short: 'IC', patterns: ['IC'], objects: 5386, note: 'NGC supplements' },
  sharpless: { label: 'Sharpless', short: 'Sh2', patterns: ['SH2', 'SH 2', 'SHARPLESS'], objects: 313, note: 'Emission nebulae' },
  barnard: { label: 'Barnard', short: 'B', patterns: ['B'], objects: 370, note: 'Dark nebulae' },
  ldnlbn: { label: 'LDN / LBN', short: 'LDN/LBN', patterns: ['LDN', 'LBN'], objects: 3000, note: 'Dark and bright nebulae' },
  caldwell: { label: 'Caldwell', short: 'C', patterns: ['C'], objects: 109, note: 'Showcase objects' },
  arp: { label: 'Arp', short: 'Arp', patterns: ['ARP'], objects: 338, note: 'Peculiar galaxies' },
  abell: { label: 'Abell', short: 'Abell', patterns: ['ABELL'], objects: 4076, note: 'Galaxy clusters and planetary nebulae' },
  pk: { label: 'Perek-Kohoutek', short: 'PK', patterns: ['PK'], objects: 1500, note: 'Planetary nebulae' }
};

const SKY_OBJECTS = [
  { id: 'm31', name: 'Andromeda Galaxy', catalogue: 'M31', type: 'galaxy', constellation: 'Andromeda', ra: 0.712, dec: 41.269, mag: 3.4, size: '190′ × 60′', season: 'Aug–Dec', focal: '135–600mm', exposure: '2–6h LRGB/OSC', guide: 'Huge bright spiral; frame with M32/M110. Great first galaxy target.' },
  { id: 'm33', name: 'Triangulum Galaxy', catalogue: 'M33', type: 'galaxy', constellation: 'Triangulum', ra: 1.565, dec: 30.66, mag: 5.7, size: '70′ × 42′', season: 'Sep–Dec', focal: '300–700mm', exposure: '4–8h', guide: 'Low-surface-brightness face-on spiral; dark transparent nights matter.' },
  { id: 'm81', name: 'Bode’s Galaxy', catalogue: 'M81', type: 'galaxy', constellation: 'Ursa Major', ra: 9.925, dec: 69.066, mag: 6.9, size: '27′ × 14′', season: 'Jan–Apr', focal: '600–1200mm', exposure: '4–8h', guide: 'Pair with M82 for a classic galaxy composition.' },
  { id: 'm51', name: 'Whirlpool Galaxy', catalogue: 'M51', type: 'galaxy', constellation: 'Canes Venatici', ra: 13.498, dec: 47.195, mag: 8.4, size: '11′ × 7′', season: 'Mar–Jun', focal: '900–1600mm', exposure: '5–10h', guide: 'Small but spectacular interacting spiral; needs good seeing and longer focal length.' },
  { id: 'm101', name: 'Pinwheel Galaxy', catalogue: 'M101', type: 'galaxy', constellation: 'Ursa Major', ra: 14.053, dec: 54.349, mag: 7.9, size: '29′ × 27′', season: 'Mar–Jun', focal: '500–1000mm', exposure: '6–12h', guide: 'Large low surface brightness galaxy; protect against moonlight and haze.' },
  { id: 'm42', name: 'Orion Nebula', catalogue: 'M42', type: 'nebula', constellation: 'Orion', ra: 5.588, dec: -5.391, mag: 4.0, size: '85′ × 60′', season: 'Nov–Feb', focal: '250–800mm', exposure: 'HDR: 10s–300s', guide: 'Bright core needs short subs plus longer dust exposures.' },
  { id: 'ic434', name: 'Horsehead & Flame', catalogue: 'IC 434 / NGC 2024', type: 'nebula', constellation: 'Orion', ra: 5.68, dec: -2.45, mag: 7.3, size: '60′ region', season: 'Nov–Feb', focal: '400–1000mm', exposure: '4–10h Ha/OIII/RGB', guide: 'Hydrogen-alpha rich winter target; avoid heavy moon unless shooting narrowband.' },
  { id: 'ngc7000', name: 'North America Nebula', catalogue: 'NGC 7000', type: 'nebula', constellation: 'Cygnus', ra: 20.98, dec: 44.33, mag: 4.0, size: '120′ × 100′', season: 'Jun–Oct', focal: '135–400mm', exposure: '3–8h Ha/OIII/RGB', guide: 'Large emission nebula; include Pelican at wide focal lengths.' },
  { id: 'veil', name: 'Veil Nebula', catalogue: 'NGC 6960/6992', type: 'nebula', constellation: 'Cygnus', ra: 20.75, dec: 30.7, mag: 7.0, size: '3° complex', season: 'Jun–Oct', focal: '135–500mm', exposure: '4–10h OIII/Ha', guide: 'Supernova remnant with strong OIII; wide mosaics look excellent.' },
  { id: 'rosette', name: 'Rosette Nebula', catalogue: 'NGC 2237', type: 'nebula', constellation: 'Monoceros', ra: 6.53, dec: 5.05, mag: 9.0, size: '80′ × 60′', season: 'Dec–Mar', focal: '300–700mm', exposure: '4–10h narrowband', guide: 'Structured emission around open cluster NGC 2244.' },
  { id: 'california', name: 'California Nebula', catalogue: 'NGC 1499', type: 'nebula', constellation: 'Perseus', ra: 4.05, dec: 36.37, mag: 6.0, size: '145′ × 40′', season: 'Oct–Feb', focal: '135–400mm', exposure: '4–8h Ha/RGB', guide: 'Very wide red emission arc near Xi Persei.' },
  { id: 'lagoon', name: 'Lagoon Nebula', catalogue: 'M8', type: 'nebula', constellation: 'Sagittarius', ra: 18.061, dec: -24.386, mag: 6.0, size: '90′ × 40′', season: 'Jun–Aug', focal: '300–800mm', exposure: '2–6h', guide: 'Low from the UK; catch at southern culmination on transparent nights.' },
  { id: 'eagle', name: 'Eagle Nebula', catalogue: 'M16', type: 'nebula', constellation: 'Serpens', ra: 18.313, dec: -13.807, mag: 6.4, size: '35′ × 28′', season: 'Jun–Aug', focal: '700–1400mm', exposure: '4–8h narrowband', guide: 'Pillars of Creation target; benefits from focal length and good altitude.' },
  { id: 'm45', name: 'Pleiades', catalogue: 'M45', type: 'cluster', constellation: 'Taurus', ra: 3.792, dec: 24.105, mag: 1.6, size: '110′', season: 'Oct–Feb', focal: '200–600mm', exposure: '3–8h broadband', guide: 'Blue reflection nebulosity; avoid moon and stretch gently.' },
  { id: 'double', name: 'Double Cluster', catalogue: 'NGC 869/884', type: 'cluster', constellation: 'Perseus', ra: 2.35, dec: 57.13, mag: 4.3, size: '60′', season: 'Aug–Feb', focal: '250–700mm', exposure: '1–4h broadband', guide: 'Rich star fields; excellent colour calibration and refractor target.' },
  { id: 'm44', name: 'Beehive Cluster', catalogue: 'M44', type: 'cluster', constellation: 'Cancer', ra: 8.667, dec: 19.667, mag: 3.7, size: '95′', season: 'Jan–Apr', focal: '200–600mm', exposure: '1–3h', guide: 'Large bright cluster; works even when galaxy transparency is mediocre.' },
  { id: 'm13', name: 'Great Hercules Cluster', catalogue: 'M13', type: 'globular', constellation: 'Hercules', ra: 16.695, dec: 36.461, mag: 5.8, size: '20′', season: 'May–Sep', focal: '700–1600mm', exposure: '1–4h', guide: 'Resolve the core with good focus and short enough subs to avoid saturation.' },
  { id: 'm3', name: 'M3 Globular Cluster', catalogue: 'M3', type: 'globular', constellation: 'Canes Venatici', ra: 13.704, dec: 28.377, mag: 6.2, size: '18′', season: 'Mar–Jun', focal: '700–1600mm', exposure: '1–4h', guide: 'Dense spring globular; good moon-tolerant target compared with galaxies.' },
  { id: 'm15', name: 'Pegasus Cluster', catalogue: 'M15', type: 'globular', constellation: 'Pegasus', ra: 21.5, dec: 12.167, mag: 6.2, size: '18′', season: 'Aug–Nov', focal: '700–1600mm', exposure: '1–4h', guide: 'Compact bright globular; useful autumn target between nebula runs.' },
  { id: 'm27', name: 'Dumbbell Nebula', catalogue: 'M27', type: 'planetary', constellation: 'Vulpecula', ra: 19.993, dec: 22.721, mag: 7.5, size: '8′ × 6′', season: 'Jun–Oct', focal: '800–1800mm', exposure: '3–8h OIII/Ha/RGB', guide: 'Bright planetary nebula; excellent OIII signal and detail at longer focal length.' },
  { id: 'm57', name: 'Ring Nebula', catalogue: 'M57', type: 'planetary', constellation: 'Lyra', ra: 18.894, dec: 33.029, mag: 8.8, size: '1.4′ × 1′', season: 'Jun–Sep', focal: '1500mm+', exposure: '2–6h', guide: 'Very small; best for high focal length, drizzle, and excellent seeing.' },
  { id: 'ngc7293', name: 'Helix Nebula', catalogue: 'NGC 7293 / C63 / PK 036-57.1', type: 'planetary', constellation: 'Aquarius', ra: 22.492, dec: -20.837, mag: 7.6, size: '25′', season: 'Aug–Oct', focal: '500–1200mm', exposure: '4–10h OIII/Ha', guide: 'Low UK altitude; strong Southern Hemisphere planetary nebula when it climbs.' },
  { id: 'omega-centauri', name: 'Omega Centauri', catalogue: 'NGC 5139 / C80', type: 'globular', constellation: 'Centaurus', ra: 13.447, dec: -47.479, mag: 3.7, size: '36′', season: 'Mar–Jun', focal: '400–1200mm', exposure: '1–4h broadband', guide: 'Huge southern globular cluster; a premier Brisbane/Queensland target and easy to frame at moderate focal length.' },
  { id: 'eta-carinae', name: 'Eta Carinae Nebula', catalogue: 'NGC 3372 / C92', type: 'nebula', constellation: 'Carina', ra: 10.752, dec: -59.867, mag: 1.0, size: '120′ × 120′', season: 'Feb–May', focal: '135–600mm', exposure: '2–8h Ha/OIII/RGB', guide: 'Flagship southern emission complex; exceptional from Brisbane and wider Queensland dark sites.' },
  { id: 'southern-pleiades', name: 'Southern Pleiades', catalogue: 'IC 2602 / C102', type: 'cluster', constellation: 'Carina', ra: 10.717, dec: -64.4, mag: 1.9, size: '50′', season: 'Feb–May', focal: '135–500mm', exposure: '1–3h broadband', guide: 'Bright southern open cluster; excellent travel-planning target for short broadband integrations.' },
  { id: 'running-chicken', name: 'Running Chicken Nebula', catalogue: 'IC 2944 / C100', type: 'nebula', constellation: 'Centaurus', ra: 11.65, dec: -63.02, mag: 4.5, size: '75′', season: 'Mar–Jun', focal: '250–800mm', exposure: '3–8h Ha/RGB', guide: 'Southern emission nebula and cluster field; strong target from Brisbane in autumn/winter.' },
  { id: 'tarantula', name: 'Tarantula Nebula', catalogue: 'NGC 2070 / C103', type: 'nebula', constellation: 'Dorado', ra: 5.645, dec: -69.1, mag: 8.0, size: '40′', season: 'Nov–Mar', focal: '400–1200mm', exposure: '4–10h Ha/OIII/RGB', guide: 'Large Magellanic Cloud star-forming region; far-southern showpiece for travel planning.' },
  { id: 'jewel-box', name: 'Jewel Box Cluster', catalogue: 'NGC 4755 / C94', type: 'cluster', constellation: 'Crux', ra: 12.89, dec: -60.36, mag: 4.2, size: '10′', season: 'Mar–Jun', focal: '600–1600mm', exposure: '1–3h broadband', guide: 'Compact colourful open cluster near the Southern Cross; excellent from Australian latitudes.' },
  { id: 'lambda-centauri', name: 'Lambda Centauri Nebula', catalogue: 'IC 2948', type: 'nebula', constellation: 'Centaurus', ra: 11.67, dec: -63.0, mag: 6.0, size: '60′ region', season: 'Mar–Jun', focal: '300–900mm', exposure: '3–8h Ha/RGB', guide: 'Emission-rich Centaurus field close to Running Chicken; useful southern framing target.' },
  { id: 'blue-horsehead', name: 'Blue Horsehead Nebula', catalogue: 'IC 4592', type: 'nebula', constellation: 'Scorpius', ra: 16.2, dec: -19.45, mag: 10.0, size: '150′ region', season: 'May–Aug', focal: '135–400mm', exposure: '6–12h broadband', guide: 'Large reflection nebula in Scorpius; much more practical from southern latitudes than the UK.' },
  { id: 'prawn-nebula', name: 'Prawn Nebula', catalogue: 'IC 4628', type: 'nebula', constellation: 'Scorpius', ra: 16.95, dec: -40.33, mag: 7.3, size: '90′', season: 'May–Aug', focal: '250–700mm', exposure: '4–10h Ha/RGB', guide: 'Bright southern emission nebula near the Scorpius tail; strong Queensland winter target.' },
  { id: 'tulip', name: 'Tulip Nebula', catalogue: 'Sh2-101', type: 'nebula', constellation: 'Cygnus', ra: 19.997, dec: 35.33, mag: 9.0, size: '16′ × 9′', season: 'Jun–Oct', focal: '500–1200mm', exposure: '5–12h Ha/OIII/RGB', guide: 'Sharpless emission nebula near Cygnus X-1; strong narrowband target for summer/autumn.' },
  { id: 'bubble', name: 'Bubble Nebula', catalogue: 'NGC 7635 / C11 / Sh2-162', type: 'nebula', constellation: 'Cassiopeia', ra: 23.347, dec: 61.2, mag: 10.0, size: '15′ × 8′', framingSize: '48′ × 34′ region', season: 'Aug–Dec', focal: '450–1600mm', exposure: '6–12h Ha/OIII/RGB', guide: 'Compact emission bubble in a wider Sh2-162/M52 Cassiopeia field; use longer focal length for the bubble itself or 400–600mm for the full red emission region.' },
  { id: 'b33', name: 'Horsehead Dark Nebula', catalogue: 'B33 / IC 434', type: 'nebula', constellation: 'Orion', ra: 5.683, dec: -2.46, mag: 7.3, size: '8′ × 6′', season: 'Nov–Feb', focal: '600–1200mm', exposure: '4–10h Ha/RGB', guide: 'Barnard dark nebula silhouetted against IC 434; needs careful contrast and good transparency.' },
  { id: 'ldn1622', name: 'Boogie Man Nebula', catalogue: 'LDN 1622', type: 'nebula', constellation: 'Orion', ra: 5.9, dec: 1.83, mag: 10.0, size: '30′', season: 'Nov–Feb', focal: '400–900mm', exposure: '6–14h broadband/Ha', guide: 'Dark nebula with faint surrounding emission; benefits from dark skies and long integration.' },
  { id: 'lbn437', name: 'Gecko Nebula', catalogue: 'LBN 437', type: 'nebula', constellation: 'Lacerta', ra: 22.533, dec: 37.75, mag: 10.0, size: '45′ region', season: 'Aug–Nov', focal: '400–900mm', exposure: '8–16h broadband/Ha', guide: 'Faint bright/dark nebula complex in Lacerta; a deeper project target.' },
  { id: 'cocoon', name: 'Cocoon Nebula', catalogue: 'IC 5146 / C19 / LBN 424', type: 'nebula', constellation: 'Cygnus', ra: 21.892, dec: 47.267, mag: 7.2, size: '12′', season: 'Jul–Nov', focal: '500–1200mm', exposure: '5–12h Ha/RGB', guide: 'Caldwell/IC nebula with dark nebula streamers; strong framing target in Cygnus.' },
  { id: 'arp273', name: 'Rose Galaxies', catalogue: 'Arp 273 / UGC 1810', type: 'galaxy', constellation: 'Andromeda', ra: 2.355, dec: 39.367, mag: 13.0, size: '1.5′ pair', season: 'Sep–Dec', focal: '1500mm+', exposure: '8–20h LRGB', guide: 'Tiny peculiar interacting galaxy pair; advanced long focal-length/seeing target.' },
  { id: 'abell1656', name: 'Coma Cluster', catalogue: 'Abell 1656', type: 'galaxy', constellation: 'Coma Berenices', ra: 12.998, dec: 27.98, mag: 12.5, size: '2° cluster', season: 'Mar–Jun', focal: '600–1600mm', exposure: '8–20h LRGB', guide: 'Rich Abell galaxy cluster; excellent deep galaxy-field project under transparent spring skies.' },
  { id: 'abell39', name: 'Abell 39', catalogue: 'Abell 39 / PK 047+42.1', type: 'planetary', constellation: 'Hercules', ra: 16.458, dec: 27.907, mag: 13.7, size: '3′', season: 'May–Sep', focal: '1000mm+', exposure: '8–20h OIII/RGB', guide: 'Very faint spherical planetary nebula; advanced OIII target requiring dark/transparent conditions.' }
];

const TARGET_IMAGES = {
  eagle: '/assets/sky-targets/eagle.jpg',
  ic434: '/assets/sky-targets/ic434.jpg',
  lagoon: '/assets/sky-targets/lagoon.jpg',
  m101: '/assets/sky-targets/m101.jpg',
  m13: '/assets/sky-targets/m13.jpg',
  m15: '/assets/sky-targets/m15.jpg',
  m27: '/assets/sky-targets/m27.jpg',
  m3: '/assets/sky-targets/m3.jpg',
  m31: '/assets/sky-targets/m31.png',
  m33: '/assets/sky-targets/m33.jpg',
  m42: '/assets/sky-targets/m42.jpg',
  m44: '/assets/sky-targets/m44.jpg',
  m45: '/assets/sky-targets/m45.jpg',
  m51: '/assets/sky-targets/m51.jpg',
  m57: '/assets/sky-targets/m57.jpg',
  m81: '/assets/sky-targets/m81.jpg',
  ngc7000: '/assets/sky-targets/ngc7000.jpg',
  ngc7293: '/assets/sky-targets/ngc7293.jpg',
  rosette: '/assets/sky-targets/rosette.jpg',
  tulip: '/assets/sky-targets/tulip.jpg',
  veil: '/assets/sky-targets/veil.jpg',
  double: '/assets/sky-targets/double.jpg',
  california: '/assets/sky-targets/california.jpg',
  bubble: '/assets/sky-targets/bubble.jpg',
  b33: '/assets/sky-targets/b33.jpg',
  ldn1622: '/assets/sky-targets/ldn1622.jpg',
  lbn437: '/assets/sky-targets/lbn437.jpg',
  cocoon: '/assets/sky-targets/cocoon.jpg',
  arp273: '/assets/sky-targets/arp273.jpg',
  abell1656: '/assets/sky-targets/abell1656.jpg',
  abell39: '/assets/sky-targets/abell39.jpg'
};

const TYPE_IMAGE_FALLBACK = {
  galaxy: TARGET_IMAGES.m31,
  nebula: TARGET_IMAGES.m42,
  cluster: TARGET_IMAGES.m45,
  globular: TARGET_IMAGES.m15,
  planetary: TARGET_IMAGES.m57
};

const DEFAULT_VIEW = { yaw: 2.42, pitch: -0.65, zoom: 3.0 };
const DEFAULT_VIEWER_LOCATION = { label: 'Northumberland, UK', latitude: 55.4157, longitude: -1.7066 };
const PLANNER_STORAGE_KEY = 'astro-vino-sky-map-planner-v1';
// Camera dimensions are the active sensor width/height in millimetres.
// Where vendors publish pixel size + active resolution, derive dimensions as:
//   sensor_mm = active_pixels * pixel_size_um / 1000
// Source examples checked 2026-08-01:
// - Player One Uranus-C Pro official page: IMX585, 3856 × 2180, 2.9µm, 12.85mm diagonal.
// - ZWO 533/294/2600 class presets use the published active resolutions and pixel sizes below.
const CAMERA_PRESETS = {
  'uranus-c-pro': { label: 'Player One Uranus-C Pro', sensorWidth: 11.1824, sensorHeight: 6.322, pixelSize: 2.9, resolution: '3856 × 2180' },
  'asi533mc': { label: 'ZWO ASI533MC / 533-class', sensorWidth: 11.3101, sensorHeight: 11.3101, pixelSize: 3.76, resolution: '3008 × 3008' },
  'asi294mc': { label: 'ZWO ASI294MC / 294-class', sensorWidth: 19.1867, sensorHeight: 13.0659, pixelSize: 4.63, resolution: '4144 × 2822' },
  'asi2600mc': { label: 'APS-C 2600-class', sensorWidth: 23.4925, sensorHeight: 15.7018, pixelSize: 3.76, resolution: '6248 × 4176' },
  'full-frame': { label: 'Full-frame 36 × 24mm', sensorWidth: 36, sensorHeight: 24, pixelSize: 5.94, resolution: '6060 × 4040' },
  custom: { label: 'Custom camera', sensorWidth: 11.3101, sensorHeight: 11.3101, pixelSize: 3.76, resolution: 'Custom' }
};
const CAMERA_PRESET_KEYS = Object.keys(CAMERA_PRESETS);
// Telescope presets are aperture/focal-length inputs plus the optical-train multiplier.
// Askar 80PHQ official native spec: 80mm aperture, 600mm focal length, f/7.5.
// Askar/Sharpstar 80PHQ 0.76× reducer official spec: 456mm effective focal length, f/5.7.
const SCOPE_PRESETS = {
  'askar-80phq': { label: 'Askar 80PHQ', focalLength: 600, aperture: 80, reducer: 1 },
  'askar-80phq-reducer': { label: 'Askar 80PHQ + 0.76× reducer', focalLength: 600, aperture: 80, reducer: 0.76 },
  'askar-sqa55': { label: 'Askar SQA55', focalLength: 264, aperture: 55, reducer: 1 },
  'stellalyra-10': { label: 'StellaLyra 10" f/4', focalLength: 1000, aperture: 254, reducer: 1 },
  'stellalyra-coma': { label: 'StellaLyra 10" + 0.95× corrector', focalLength: 1000, aperture: 254, reducer: 0.95 },
  custom: { label: 'Custom telescope', focalLength: 600, aperture: 80, reducer: 1 }
};
const SCOPE_PRESET_KEYS = Object.keys(SCOPE_PRESETS);
const DEFAULT_OBSERVING_WINDOW_START_HOUR = 20;
const DEFAULT_OBSERVING_WINDOW_END_HOUR = 4;
const OBSERVING_WINDOW_HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => hour);

const STAR_FIELD = Array.from({ length: 260 }, (_, index) => {
  const golden = 2.399963229728653;
  const y = 1 - (index / 259) * 2;
  const radius = Math.sqrt(Math.max(0, 1 - y * y));
  const theta = index * golden;
  return {
    x: Math.cos(theta) * radius,
    y,
    z: Math.sin(theta) * radius,
    alpha: 0.22 + ((index * 37) % 60) / 100,
    size: 0.55 + ((index * 17) % 32) / 18
  };
});

function catalogueKeysForObject(object) {
  const text = object.catalogue.toUpperCase();
  return Object.entries(CATALOGUE_FILTERS)
    .filter(([, config]) => config.patterns.some((pattern) => new RegExp(`(^|[^A-Z0-9])${pattern.replace(' ', '\\s*')}\\s*-?\\s*\\d+`).test(text)))
    .map(([key]) => key);
}

function catalogueSummaryLabel(object) {
  const keys = catalogueKeysForObject(object);
  return keys.map((key) => CATALOGUE_FILTERS[key].short).join(' / ') || 'Other';
}

function maxAltitudeForLatitude(object, latitude) {
  if (typeof latitude !== 'number') return null;
  return 90 - Math.abs(latitude - object.dec);
}

function hemisphereForLatitude(latitude) {
  return latitude >= 0 ? 'north' : 'south';
}

function hemisphereName(hemisphere) {
  return hemisphere === 'south' ? 'Southern Hemisphere' : 'Northern Hemisphere';
}

function inHemisphereBand(object, hemisphere) {
  // Keep the equatorial crossover band: many excellent targets just across the
  // celestial equator are still practical from either Earth hemisphere.
  return hemisphere === 'south' ? object.dec <= 30 : object.dec >= -30;
}

function objectVector(object) {
  const ra = (object.ra / 24) * Math.PI * 2;
  const dec = (object.dec / 180) * Math.PI;
  return {
    x: Math.cos(dec) * Math.cos(ra),
    y: Math.sin(dec),
    z: Math.cos(dec) * Math.sin(ra)
  };
}

function todayIsoDate() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function observingWindowDuration(startHour, endHour) {
  const duration = (Number(endHour) - Number(startHour) + 24) % 24;
  return duration || 24;
}

function formatClockHour(hourValue) {
  const totalMinutes = Math.round(Number(hourValue) * 60);
  const hours = ((Math.floor(totalMinutes / 60) % 24) + 24) % 24;
  const minutes = ((totalMinutes % 60) + 60) % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function observingDateAtTime(observingDate, offsetHours = 0, startHour = DEFAULT_OBSERVING_WINDOW_START_HOUR) {
  const [year, month, day] = String(observingDate || todayIsoDate()).split('-').map(Number);
  const base = Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)
    ? new Date(year, month - 1, day)
    : new Date();
  base.setHours(0, 0, 0, 0);
  const localHours = Number(startHour) + Number(offsetHours);
  base.setDate(base.getDate() + Math.floor(localHours / 24));
  base.setHours(Math.floor(localHours) % 24, Math.round((localHours % 1) * 60), 0, 0);
  return base;
}

function localSiderealHours(offsetHours, longitudeDegrees, observingDate, startHour = DEFAULT_OBSERVING_WINDOW_START_HOUR) {
  const base = observingDateAtTime(observingDate, offsetHours, startHour);
  const jd = base.getTime() / 86400000 + 2440587.5;
  const d = jd - 2451545.0;
  const gmst = 18.697374558 + 24.06570982441908 * d;
  return ((gmst + longitudeDegrees / 15) % 24 + 24) % 24;
}

function equatorialToLocalVectorFromRaDec(raHours, decDegrees, timeOffsetHours, latitudeDegrees, longitudeDegrees = 0, observingDate = todayIsoDate(), startHour = DEFAULT_OBSERVING_WINDOW_START_HOUR) {
  const dec = (decDegrees / 180) * Math.PI;
  const lat = (latitudeDegrees / 180) * Math.PI;
  const lst = localSiderealHours(timeOffsetHours, longitudeDegrees, observingDate, startHour);
  const hourAngle = ((lst - raHours) / 24) * Math.PI * 2;
  const sinAlt = Math.sin(dec) * Math.sin(lat) + Math.cos(dec) * Math.cos(lat) * Math.cos(hourAngle);
  const y = Math.max(-1, Math.min(1, sinAlt));
  return {
    x: -Math.cos(dec) * Math.sin(hourAngle),
    y,
    z: Math.sin(dec) * Math.cos(lat) - Math.cos(dec) * Math.sin(lat) * Math.cos(hourAngle),
    altitude: Math.asin(y) * (180 / Math.PI)
  };
}

function equatorialPointToLocalVector(point, timeOffsetHours, latitudeDegrees, longitudeDegrees = 0, observingDate = todayIsoDate(), startHour = DEFAULT_OBSERVING_WINDOW_START_HOUR) {
  const ra = Math.atan2(point.z, point.x) / (Math.PI * 2) * 24;
  const dec = Math.asin(Math.max(-1, Math.min(1, point.y))) * (180 / Math.PI);
  return equatorialToLocalVectorFromRaDec(ra < 0 ? ra + 24 : ra, dec, timeOffsetHours, latitudeDegrees, longitudeDegrees, observingDate, startHour);
}

function rotatePoint(point, yaw, pitch) {
  const cy = Math.cos(yaw);
  const sy = Math.sin(yaw);
  const cp = Math.cos(pitch);
  const sp = Math.sin(pitch);
  const x1 = point.x * cy - point.z * sy;
  const z1 = point.x * sy + point.z * cy;
  const y1 = point.y * cp - z1 * sp;
  const z2 = point.y * sp + z1 * cp;
  return { x: x1, y: y1, z: z2 };
}

function projectSpherePoint(point, width, height, zoom) {
  const depth = 2.85;
  const scale = (Math.min(width, height) * 0.36 * zoom) / (depth - point.z * 0.74);
  return {
    x: width / 2 + point.x * scale,
    y: height / 2 - point.y * scale,
    scale,
    visible: point.z > -1.22,
    depth: point.z
  };
}

function projectSphereObject(object, width, height, timeOffsetHours, yaw, pitch, zoom, latitudeDegrees, longitudeDegrees, observingDate, startHour = DEFAULT_OBSERVING_WINDOW_START_HOUR) {
  const local = equatorialToLocalVectorFromRaDec(object.ra, object.dec, timeOffsetHours, latitudeDegrees, longitudeDegrees, observingDate, startHour);
  const rotated = rotatePoint(local, yaw, pitch);
  return { ...projectSpherePoint(rotated, width, height, zoom), altitude: local.altitude, aboveHorizon: local.altitude >= 0 };
}

function formatEveningTime(offsetHours, startHour = DEFAULT_OBSERVING_WINDOW_START_HOUR) {
  return formatClockHour(Number(startHour) + Number(offsetHours));
}

function formatRa(raHours) {
  if (!Number.isFinite(Number(raHours))) return '—';
  const totalMinutes = Math.round(raHours * 60);
  const hours = Math.floor(totalMinutes / 60) % 24;
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m`;
}

function formatDec(decDegrees) {
  if (!Number.isFinite(Number(decDegrees))) return '—';
  const sign = decDegrees >= 0 ? '+' : '−';
  const absolute = Math.abs(decDegrees);
  const degrees = Math.floor(absolute);
  const minutes = Math.round((absolute - degrees) * 60);
  return `${sign}${degrees}° ${String(minutes).padStart(2, '0')}′`;
}

function parseTargetSizeArcMinutes(sizeText) {
  if (!sizeText) return { width: null, height: null, label: 'Size unknown' };
  const cleaned = String(sizeText).toLowerCase().replace(/region|complex|pair/g, '').trim();
  const numbers = cleaned.match(/\d+(?:\.\d+)?/g)?.map(Number) || [];
  if (!numbers.length) return { width: null, height: null, label: sizeText };
  const multiplier = cleaned.includes('°') ? 60 : 1;
  const width = numbers[0] * multiplier;
  const height = (numbers[1] ?? numbers[0]) * multiplier;
  return { width, height, label: sizeText };
}

function formatArcMinutes(value) {
  if (!Number.isFinite(Number(value))) return '—';
  if (value >= 60) return `${(value / 60).toFixed(value >= 180 ? 1 : 2)}°`;
  if (value >= 10) return `${value.toFixed(0)}′`;
  return `${value.toFixed(1)}′`;
}

function safeNumber(value, fallback) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : fallback;
}

function moonPhaseLabel(phase) {
  if (phase < 0.03 || phase > 0.97) return 'New Moon';
  if (phase < 0.22) return 'Waxing crescent';
  if (phase < 0.28) return 'First quarter';
  if (phase < 0.47) return 'Waxing gibbous';
  if (phase < 0.53) return 'Full Moon';
  if (phase < 0.72) return 'Waning gibbous';
  if (phase < 0.78) return 'Last quarter';
  return 'Waning crescent';
}

const PLANET_STYLES = {
  Mercury: { color: '#b9c3cb', short: '☿' },
  Venus: { color: '#ffe4a8', short: '♀' },
  Mars: { color: '#ff8c5f', short: '♂' },
  Jupiter: { color: '#ffd08a', short: '♃' },
  Saturn: { color: '#f4d48b', short: '♄' },
  Uranus: { color: '#8be7ff', short: '♅' },
  Neptune: { color: '#7aa6ff', short: '♆' }
};

const SOLAR_SYSTEM_IMAGES = {
  moon: '/assets/solar-system/moon.jpg',
  mercury: '/assets/solar-system/mercury.jpg',
  venus: '/assets/solar-system/venus.jpg',
  mars: '/assets/solar-system/mars.jpg',
  jupiter: '/assets/solar-system/jupiter.jpg',
  saturn: '/assets/solar-system/saturn.jpg',
  uranus: '/assets/solar-system/uranus.jpg',
  neptune: '/assets/solar-system/neptune.jpg',
  comet: '/assets/solar-system/comet-12p-photo.jpg'
};

const ISS_TLE = {
  name: 'ISS (ZARYA)',
  line1: '1 25544U 98067A   26209.15252568  .00010831  00000+0  20282-3 0  9992',
  line2: '2 25544  51.6320  97.3682 0007093 345.6120  14.4666 15.49220842578109'
};

const DYNAMIC_OVERLAY_STYLES = {
  iss: { color: '#72f0ff', short: 'ISS' },
  custom: { color: '#76ffb2', short: '☄' }
};

const REAL_COMETS = {
  '12p': {
    id: 'comet-12p',
    name: '12P/Pons-Brooks',
    label: '12P',
    elements: { epoch: 2460211.5, e: 0.9545612442767357, a: 17.18491452314557, i: 74.19091017013747, om: 255.8553510995133, w: 198.9879994677832, ma: 357.0928096635318, n: 0.01383512251776258 },
    description: 'Real periodic comet from JPL SBDB orbital elements. Use the selected night/time/location to inspect its apparent path.'
  },
  '2p': {
    id: 'comet-2p',
    name: '2P/Encke',
    label: '2P',
    elements: { epoch: 2459847.5, e: 0.8477496967533629, a: 2.219688710074586, i: 11.41227811179314, om: 334.1935846036774, w: 187.1342463695676, ma: 243.1260693210057, n: 0.2980341047245246 },
    description: 'Real periodic comet from JPL SBDB orbital elements. Use the selected night/time/location to inspect its apparent path.'
  },
  '67p': {
    id: 'comet-67p',
    name: '67P/Churyumov-Gerasimenko',
    label: '67P',
    elements: { epoch: 2457305.5, e: 0.6409081306555051, a: 3.462249489765068, i: 7.040294906760007, om: 50.13557380441372, w: 12.79824973415729, ma: 8.859927418758764, n: 0.1529912292115438 },
    description: 'Real periodic comet from JPL SBDB orbital elements. Use the selected night/time/location to inspect its apparent path.'
  }
};

function normalizeDegrees(value) {
  return ((value % 360) + 360) % 360;
}

function degreesToRadians(value) {
  return (value / 180) * Math.PI;
}

function radiansToDegrees(value) {
  return (value / Math.PI) * 180;
}

function dateToJulianDay(date) {
  return date.getTime() / 86400000 + 2440587.5;
}

function altAzToLocalVector(altitudeRadians, sunCalcAzimuthRadians) {
  const azimuthFromNorth = sunCalcAzimuthRadians + Math.PI;
  const cosAlt = Math.cos(altitudeRadians);
  return {
    x: cosAlt * Math.sin(azimuthFromNorth),
    y: Math.sin(altitudeRadians),
    z: cosAlt * Math.cos(azimuthFromNorth),
    altitude: radiansToDegrees(altitudeRadians)
  };
}

function altAzFromNorthToLocalVector(altitudeRadians, azimuthFromNorthRadians) {
  const cosAlt = Math.cos(altitudeRadians);
  return {
    x: cosAlt * Math.sin(azimuthFromNorthRadians),
    y: Math.sin(altitudeRadians),
    z: cosAlt * Math.cos(azimuthFromNorthRadians),
    altitude: radiansToDegrees(altitudeRadians)
  };
}

function raDecFromEci(positionEci) {
  const radius = Math.sqrt(positionEci.x * positionEci.x + positionEci.y * positionEci.y + positionEci.z * positionEci.z);
  const ra = ((Math.atan2(positionEci.y, positionEci.x) / (Math.PI * 2)) * 24 + 24) % 24;
  const dec = radiansToDegrees(Math.asin(Math.max(-1, Math.min(1, positionEci.z / radius))));
  return { ra, dec };
}

function computeIssSkyObject(date, latitudeDegrees, longitudeDegrees) {
  try {
    const satrec = satellite.twoline2satrec(ISS_TLE.line1, ISS_TLE.line2);
    const propagated = satellite.propagate(satrec, date);
    if (!propagated?.position || typeof propagated.position === 'boolean') return null;
    const gmst = satellite.gstime(date);
    const positionEcf = satellite.eciToEcf(propagated.position, gmst);
    const look = satellite.ecfToLookAngles({
      latitude: degreesToRadians(latitudeDegrees),
      longitude: degreesToRadians(longitudeDegrees),
      height: 0
    }, positionEcf);
    const local = altAzFromNorthToLocalVector(look.elevation, look.azimuth);
    const { ra, dec } = raDecFromEci(propagated.position);
    return {
      id: 'orbital-iss',
      name: 'International Space Station',
      label: 'ISS',
      kind: 'iss',
      style: DYNAMIC_OVERLAY_STYLES.iss,
      local,
      altitude: local.altitude,
      ra,
      dec,
      rangeKm: Math.round(look.rangeSat),
      magnitude: null,
      description: 'ISS position from TLE propagation for the selected date/time/location. Toggle on only when you want orbital-object context.'
    };
  } catch (error) {
    return null;
  }
}

function cometRaDec(comet, date) {
  const d = dateToJulianDay(date);
  const el = comet.elements;
  const earth = heliocentricEclipticPosition('Earth', d - 2451543.5);
  const N = degreesToRadians(el.om);
  const i = degreesToRadians(el.i);
  const w = degreesToRadians(el.w);
  const M = degreesToRadians(normalizeDegrees(el.ma + el.n * (d - el.epoch)));
  let E = M + el.e * Math.sin(M) * (1 + el.e * Math.cos(M));
  for (let step = 0; step < 8; step += 1) {
    E -= (E - el.e * Math.sin(E) - M) / (1 - el.e * Math.cos(E));
  }
  const xv = el.a * (Math.cos(E) - el.e);
  const yv = el.a * Math.sqrt(Math.max(0, 1 - el.e * el.e)) * Math.sin(E);
  const v = Math.atan2(yv, xv);
  const r = Math.sqrt(xv * xv + yv * yv);
  const xh = r * (Math.cos(N) * Math.cos(v + w) - Math.sin(N) * Math.sin(v + w) * Math.cos(i));
  const yh = r * (Math.sin(N) * Math.cos(v + w) + Math.cos(N) * Math.sin(v + w) * Math.cos(i));
  const zh = r * (Math.sin(v + w) * Math.sin(i));
  const xg = xh - earth.x;
  const yg = yh - earth.y;
  const zg = zh - earth.z;
  const obliquity = degreesToRadians(23.4393 - 3.563e-7 * (d - 2451543.5));
  const xe = xg;
  const ye = yg * Math.cos(obliquity) - zg * Math.sin(obliquity);
  const ze = yg * Math.sin(obliquity) + zg * Math.cos(obliquity);
  return {
    ra: ((Math.atan2(ye, xe) / (Math.PI * 2)) * 24 + 24) % 24,
    dec: radiansToDegrees(Math.atan2(ze, Math.sqrt(xe * xe + ye * ye)))
  };
}

function computeRealCometObject(comet, offset, latitudeDegrees, longitudeDegrees, observingDate, startHour) {
  const date = observingDateAtTime(observingDate, offset, startHour);
  const { ra, dec } = cometRaDec(comet, date);
  const local = equatorialToLocalVectorFromRaDec(ra, dec, offset, latitudeDegrees, longitudeDegrees, observingDate, startHour);
  return {
    id: comet.id,
    name: comet.name,
    label: comet.label,
    kind: 'custom',
    style: DYNAMIC_OVERLAY_STYLES.custom,
    image: SOLAR_SYSTEM_IMAGES.comet,
    local,
    altitude: local.altitude,
    ra,
    dec,
    magnitude: null,
    description: comet.description
  };
}

function planetElements(name, d) {
  const elements = {
    Earth: { N: 0, i: 0, w: 282.9404 + 4.70935e-5 * d, a: 1, e: 0.016709 - 1.151e-9 * d, M: 356.0470 + 0.9856002585 * d },
    Mercury: { N: 48.3313 + 3.24587e-5 * d, i: 7.0047 + 5.00e-8 * d, w: 29.1241 + 1.01444e-5 * d, a: 0.387098, e: 0.205635 + 5.59e-10 * d, M: 168.6562 + 4.0923344368 * d },
    Venus: { N: 76.6799 + 2.46590e-5 * d, i: 3.3946 + 2.75e-8 * d, w: 54.8910 + 1.38374e-5 * d, a: 0.723330, e: 0.006773 - 1.302e-9 * d, M: 48.0052 + 1.6021302244 * d },
    Mars: { N: 49.5574 + 2.11081e-5 * d, i: 1.8497 - 1.78e-8 * d, w: 286.5016 + 2.92961e-5 * d, a: 1.523688, e: 0.093405 + 2.516e-9 * d, M: 18.6021 + 0.5240207766 * d },
    Jupiter: { N: 100.4542 + 2.76854e-5 * d, i: 1.3030 - 1.557e-7 * d, w: 273.8777 + 1.64505e-5 * d, a: 5.20256, e: 0.048498 + 4.469e-9 * d, M: 19.8950 + 0.0830853001 * d },
    Saturn: { N: 113.6634 + 2.38980e-5 * d, i: 2.4886 - 1.081e-7 * d, w: 339.3939 + 2.97661e-5 * d, a: 9.55475, e: 0.055546 - 9.499e-9 * d, M: 316.9670 + 0.0334442282 * d },
    Uranus: { N: 74.0005 + 1.39780e-5 * d, i: 0.7733 + 1.90e-8 * d, w: 96.6612 + 3.05650e-5 * d, a: 19.18171 - 1.55e-8 * d, e: 0.047318 + 7.45e-9 * d, M: 142.5905 + 0.011725806 * d },
    Neptune: { N: 131.7806 + 3.01730e-5 * d, i: 1.7700 - 2.55e-7 * d, w: 272.8461 - 6.027e-6 * d, a: 30.05826 + 3.313e-8 * d, e: 0.008606 + 2.15e-9 * d, M: 260.2471 + 0.005995147 * d }
  };
  return elements[name];
}

function heliocentricEclipticPosition(name, d) {
  const el = planetElements(name, d);
  const N = degreesToRadians(el.N);
  const i = degreesToRadians(el.i);
  const w = degreesToRadians(el.w);
  const M = degreesToRadians(normalizeDegrees(el.M));
  let E = M + el.e * Math.sin(M) * (1 + el.e * Math.cos(M));
  for (let step = 0; step < 5; step += 1) {
    E -= (E - el.e * Math.sin(E) - M) / (1 - el.e * Math.cos(E));
  }
  const xv = el.a * (Math.cos(E) - el.e);
  const yv = el.a * Math.sqrt(1 - el.e * el.e) * Math.sin(E);
  const v = Math.atan2(yv, xv);
  const r = Math.sqrt(xv * xv + yv * yv);
  const cosN = Math.cos(N);
  const sinN = Math.sin(N);
  const cosVW = Math.cos(v + w);
  const sinVW = Math.sin(v + w);
  return {
    x: r * (cosN * cosVW - sinN * sinVW * Math.cos(i)),
    y: r * (sinN * cosVW + cosN * sinVW * Math.cos(i)),
    z: r * (sinVW * Math.sin(i))
  };
}

function planetRaDec(name, date) {
  const d = dateToJulianDay(date) - 2451543.5;
  const earth = heliocentricEclipticPosition('Earth', d);
  const planet = heliocentricEclipticPosition(name, d);
  const xg = planet.x - earth.x;
  const yg = planet.y - earth.y;
  const zg = planet.z - earth.z;
  const obliquity = degreesToRadians(23.4393 - 3.563e-7 * d);
  const xe = xg;
  const ye = yg * Math.cos(obliquity) - zg * Math.sin(obliquity);
  const ze = yg * Math.sin(obliquity) + zg * Math.cos(obliquity);
  const ra = normalizeDegrees(radiansToDegrees(Math.atan2(ye, xe))) / 15;
  const dec = radiansToDegrees(Math.atan2(ze, Math.sqrt(xe * xe + ye * ye)));
  return { ra, dec };
}

function drawMoonPhaseMarker(ctx, x, y, radius, phase, illumination) {
  ctx.save();
  ctx.shadowColor = '#d9e6ff';
  ctx.shadowBlur = radius * 1.6;
  ctx.fillStyle = 'rgba(17,22,34,0.96)';
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.clip();
  const litSide = phase <= 0.5 ? 1 : -1;
  const litWidth = Math.max(0.02, Math.min(1, illumination)) * radius * 2;
  const startX = litSide > 0 ? x + radius - litWidth : x - radius;
  const gradient = ctx.createLinearGradient(x - radius, y, x + radius, y);
  gradient.addColorStop(0, 'rgba(168,181,206,0.42)');
  gradient.addColorStop(0.55, 'rgba(244,239,213,0.92)');
  gradient.addColorStop(1, 'rgba(255,248,220,0.98)');
  ctx.fillStyle = gradient;
  ctx.fillRect(startX, y - radius, litWidth, radius * 2);
  ctx.restore();
  ctx.save();
  ctx.strokeStyle = 'rgba(230,238,255,0.9)';
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

function drawPlanetMarker(ctx, x, y, radius, color, label) {
  ctx.save();
  ctx.shadowColor = color;
  ctx.shadowBlur = radius * 1.8;
  const gradient = ctx.createRadialGradient(x - radius * 0.35, y - radius * 0.35, 0, x, y, radius * 1.25);
  gradient.addColorStop(0, '#ffffff');
  gradient.addColorStop(0.35, color);
  gradient.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(x, y, radius * 1.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.font = '900 10px Inter, Roboto, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#071020';
  ctx.fillText(label, x, y + 0.5);
  ctx.restore();
}

function drawSolarImageMarker(ctx, image, x, y, radius, color, options = {}) {
  if (!image || !image.complete || image.naturalWidth <= 0) return false;
  ctx.save();
  ctx.shadowColor = color;
  ctx.shadowBlur = radius * 1.75;
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(2,4,12,0.92)';
  ctx.fill();
  ctx.clip();
  const aspect = image.naturalWidth / image.naturalHeight;
  const drawH = radius * 2.22;
  const drawW = drawH * aspect;
  ctx.drawImage(image, x - drawW / 2, y - drawH / 2, drawW, drawH);
  if (options.phase != null) {
    const lit = Math.max(0.04, Math.min(1, options.illumination || 0.5));
    if (lit < 0.985) {
      const waxing = options.phase < 0.5;
      const shadowRadiusX = Math.max(0.5, radius * 2.05 * (1 - lit));
      ctx.globalCompositeOperation = 'multiply';
      ctx.fillStyle = `rgba(0,0,0,${Math.max(0.18, Math.min(0.62, 0.72 - lit * 0.34))})`;
      ctx.beginPath();
      ctx.ellipse(x + (waxing ? -1 : 1) * radius * (0.58 - lit * 0.34), y, shadowRadiusX, radius * 1.04, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalCompositeOperation = 'source-over';
    }
  }
  ctx.restore();
  ctx.save();
  ctx.strokeStyle = rgba(color, 0.82);
  ctx.lineWidth = 1.35;
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
  return true;
}

function rgba(hex, alpha) {
  const clean = hex.replace('#', '');
  const value = parseInt(clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean, 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

function drawGalaxy(ctx, x, y, radius, color, alpha, rotation) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, radius * 2.2);
  gradient.addColorStop(0, `rgba(255,255,255,${alpha})`);
  gradient.addColorStop(0.28, rgba(color, alpha * 0.72));
  gradient.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.ellipse(0, 0, radius * 2.2, radius * 0.72, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = `rgba(255,255,255,${alpha * 0.65})`;
  ctx.lineWidth = Math.max(0.8, radius * 0.11);
  for (let arm = 0; arm < 2; arm += 1) {
    ctx.beginPath();
    for (let i = 0; i < 42; i += 1) {
      const t = i / 41;
      const a = arm * Math.PI + t * Math.PI * 2.2;
      const r = radius * (0.22 + t * 1.45);
      const px = Math.cos(a) * r;
      const py = Math.sin(a) * r * 0.42;
      if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
    }
    ctx.stroke();
  }
  ctx.restore();
}

function drawNebula(ctx, x, y, radius, color, alpha, seed) {
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  for (let i = 0; i < 5; i += 1) {
    const a = seed + i * 1.72;
    const ox = Math.cos(a) * radius * 0.34;
    const oy = Math.sin(a * 1.31) * radius * 0.24;
    const r = radius * (1.2 - i * 0.1);
    const gradient = ctx.createRadialGradient(x + ox, y + oy, 0, x + ox, y + oy, r);
    gradient.addColorStop(0, rgba(color, alpha * (0.55 - i * 0.05)));
    gradient.addColorStop(0.48, rgba(color, alpha * 0.15));
    gradient.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.ellipse(x + ox, y + oy, r * 1.15, r * 0.72, a, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function drawCluster(ctx, x, y, radius, color, alpha, globular, seed) {
  ctx.save();
  const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius * (globular ? 2 : 1.35));
  gradient.addColorStop(0, `rgba(255,255,255,${alpha * 0.95})`);
  gradient.addColorStop(0.45, rgba(color, alpha * 0.32));
  gradient.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(x, y, radius * (globular ? 1.8 : 1.25), 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = `rgba(255,255,255,${alpha})`;
  const count = globular ? 34 : 16;
  for (let i = 0; i < count; i += 1) {
    const a = seed + i * 2.399;
    const r = radius * (globular ? Math.sqrt(i / count) * 1.12 : (0.28 + ((i * 19) % 70) / 70));
    ctx.beginPath();
    ctx.arc(x + Math.cos(a) * r, y + Math.sin(a) * r, Math.max(0.8, radius * 0.08), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function drawPlanetary(ctx, x, y, radius, color, alpha) {
  ctx.save();
  ctx.strokeStyle = rgba(color, alpha);
  ctx.lineWidth = Math.max(1.2, radius * 0.16);
  ctx.shadowColor = color;
  ctx.shadowBlur = radius;
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.stroke();
  ctx.fillStyle = `rgba(255,255,255,${alpha * 0.8})`;
  ctx.beginPath();
  ctx.arc(x, y, Math.max(1, radius * 0.18), 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

export default function StarMap() {
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const dragRef = useRef({ dragging: false, x: 0, y: 0, yaw: 0, pitch: 0 });
  const activePointersRef = useRef(new Map());
  const pinchRef = useRef({ active: false, distance: 0, zoom: DEFAULT_VIEW.zoom, moved: false });
  const hitRef = useRef([]);
  const imageCacheRef = useRef({});
  const dateInputRef = useRef(null);
  const [imagesReadyTick, setImagesReadyTick] = useState(0);
  const [yaw, setYaw] = useState(DEFAULT_VIEW.yaw);
  const [pitch, setPitch] = useState(DEFAULT_VIEW.pitch);
  const [zoom, setZoom] = useState(DEFAULT_VIEW.zoom);
  const [selectedId, setSelectedId] = useState(null);
  const [filters, setFilters] = useState(['galaxy', 'nebula', 'cluster', 'globular', 'planetary']);
  const [solarFilters, setSolarFilters] = useState(['moon', 'planet']);
  const [selectedCometKey, setSelectedCometKey] = useState('12p');
  const [catalogueFilters, setCatalogueFilters] = useState(['messier']);
  const [query, setQuery] = useState('');
  const [planner, setPlanner] = useState([]);
  const [autoRotate, setAutoRotate] = useState(false);
  const [viewerLocation, setViewerLocation] = useState(DEFAULT_VIEWER_LOCATION);
  const [hemisphereMode, setHemisphereMode] = useState('auto');
  const [onlyObservable, setOnlyObservable] = useState(true);
  const [minAltitude, setMinAltitude] = useState(20);
  const [locationMessage, setLocationMessage] = useState('');
  const [locationQuery, setLocationQuery] = useState(DEFAULT_VIEWER_LOCATION.label);
  const [locationResults, setLocationResults] = useState([]);
  const [locationSearching, setLocationSearching] = useState(false);
  const [observingDate, setObservingDate] = useState(todayIsoDate());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(todayIsoDate());
  const [timeOffset, setTimeOffset] = useState(0);
  const [observingWindowStartHour, setObservingWindowStartHour] = useState(DEFAULT_OBSERVING_WINDOW_START_HOUR);
  const [observingWindowEndHour, setObservingWindowEndHour] = useState(DEFAULT_OBSERVING_WINDOW_END_HOUR);
  const [timeLapsePlaying, setTimeLapsePlaying] = useState(false);
  const [targetViewOpen, setTargetViewOpen] = useState(false);
  const [scopePresetKey, setScopePresetKey] = useState('askar-80phq');
  const [cameraPresetKey, setCameraPresetKey] = useState('uranus-c-pro');
  const [opticalTrain, setOpticalTrain] = useState({
    focalLength: SCOPE_PRESETS['askar-80phq'].focalLength,
    aperture: SCOPE_PRESETS['askar-80phq'].aperture,
    reducer: SCOPE_PRESETS['askar-80phq'].reducer,
    sensorWidth: CAMERA_PRESETS['uranus-c-pro'].sensorWidth,
    sensorHeight: CAMERA_PRESETS['uranus-c-pro'].sensorHeight,
    pixelSize: CAMERA_PRESETS['uranus-c-pro'].pixelSize
  });

  const observingWindowDurationHours = useMemo(() => observingWindowDuration(observingWindowStartHour, observingWindowEndHour), [observingWindowEndHour, observingWindowStartHour]);
  const observingWindowMarkOffsets = useMemo(() => Array.from({ length: 5 }, (_, index) => Number(((observingWindowDurationHours / 4) * index).toFixed(2))), [observingWindowDurationHours]);
  const displayedTime = formatEveningTime(timeOffset, observingWindowStartHour);
  const observingWindowLabel = `${formatEveningTime(0, observingWindowStartHour)}–${formatEveningTime(observingWindowDurationHours, observingWindowStartHour)}`;
  const observingDateLabel = useMemo(() => observingDateAtTime(observingDate, 0, observingWindowStartHour).toLocaleDateString(undefined, {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }), [observingDate, observingWindowStartHour]);
  const observingDateDisplay = useMemo(() => observingDateAtTime(observingDate, 0, observingWindowStartHour).toLocaleDateString('en-GB'), [observingDate, observingWindowStartHour]);
  const calendarMonthDate = useMemo(() => {
    const [year, month] = String(calendarMonth || observingDate).split('-').map(Number);
    const date = new Date(Number.isFinite(year) ? year : new Date().getFullYear(), Number.isFinite(month) ? month - 1 : new Date().getMonth(), 1);
    date.setHours(12, 0, 0, 0);
    return date;
  }, [calendarMonth, observingDate]);
  const calendarMonthLabel = useMemo(() => calendarMonthDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }), [calendarMonthDate]);
  const calendarDays = useMemo(() => {
    const first = new Date(calendarMonthDate);
    const start = new Date(first);
    start.setDate(first.getDate() - ((first.getDay() + 6) % 7));
    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(start);
      date.setDate(start.getDate() + index);
      const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      return { iso, day: date.getDate(), currentMonth: date.getMonth() === calendarMonthDate.getMonth(), today: iso === todayIsoDate(), selected: iso === observingDate };
    });
  }, [calendarMonthDate, observingDate]);

  const setCalendarMonthOffset = (offset) => {
    const next = new Date(calendarMonthDate);
    next.setMonth(next.getMonth() + offset, 1);
    setCalendarMonth(`${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-01`);
  };

  const selectObservingDate = (nextDate) => {
    pauseMotionForFilterChange();
    setObservingDate(nextDate || todayIsoDate());
    setCalendarMonth(nextDate || todayIsoDate());
    setCalendarOpen(false);
    setSelectedId(null);
  };

  useEffect(() => {
    setCalendarMonth(observingDate);
  }, [observingDate]);

  const pauseMotionForFilterChange = () => {
    setAutoRotate(false);
    setTimeLapsePlaying(false);
  };

  const updateObservingWindowStart = (hour) => {
    pauseMotionForFilterChange();
    setObservingWindowStartHour(Number(hour));
    setTimeOffset(0);
    setSelectedId(null);
  };

  const updateObservingWindowEnd = (hour) => {
    pauseMotionForFilterChange();
    setObservingWindowEndHour(Number(hour));
    setTimeOffset(0);
    setSelectedId(null);
  };

  useEffect(() => {
    setTimeOffset((value) => Math.min(value, observingWindowDurationHours));
  }, [observingWindowDurationHours]);

  const selectedDeepSky = selectedId ? SKY_OBJECTS.find((object) => object.id === selectedId) || null : null;
  const inferredHemisphere = hemisphereForLatitude(viewerLocation.latitude);
  const activeHemisphere = hemisphereMode === 'auto' ? inferredHemisphere : hemisphereMode;
  const activeHemisphereLabel = hemisphereName(activeHemisphere);

  const filteredObjects = useMemo(() => {
    const q = query.trim().toLowerCase();
    return SKY_OBJECTS
      .map((object) => ({ ...object, maxAltitude: maxAltitudeForLatitude(object, viewerLocation.latitude) }))
      .filter((object) => filters.includes(object.type))
      .filter((object) => catalogueKeysForObject(object).some((key) => catalogueFilters.includes(key)))
      .filter((object) => !q || [object.name, object.catalogue, object.constellation, object.type, catalogueSummaryLabel(object)].join(' ').toLowerCase().includes(q))
      .filter((object) => !onlyObservable || inHemisphereBand(object, activeHemisphere));
  }, [activeHemisphere, catalogueFilters, filters, onlyObservable, query, viewerLocation.latitude]);

  const catalogWithVisibility = useMemo(() => SKY_OBJECTS
    .map((object) => ({ ...object, maxAltitude: maxAltitudeForLatitude(object, viewerLocation.latitude) })), [viewerLocation.latitude]);

  const currentAltitudeForObject = useCallback((object, offset = timeOffset) => (
    equatorialToLocalVectorFromRaDec(object.ra, object.dec, offset, viewerLocation.latitude, viewerLocation.longitude, observingDate, observingWindowStartHour).altitude
  ), [observingDate, observingWindowStartHour, timeOffset, viewerLocation.latitude, viewerLocation.longitude]);

  const nightWindowForObject = useCallback((object) => {
    let peakAltitude = -90;
    let firstOffsetAboveLimit = null;
    for (let offset = 0; offset <= observingWindowDurationHours + 0.001; offset += 0.25) {
      const altitude = equatorialToLocalVectorFromRaDec(object.ra, object.dec, offset, viewerLocation.latitude, viewerLocation.longitude, observingDate, observingWindowStartHour).altitude;
      if (altitude > peakAltitude) peakAltitude = altitude;
      if (firstOffsetAboveLimit === null && altitude >= minAltitude) firstOffsetAboveLimit = offset;
    }
    return { peakAltitude, firstOffsetAboveLimit };
  }, [minAltitude, observingDate, observingWindowDurationHours, observingWindowStartHour, viewerLocation.latitude, viewerLocation.longitude]);

  const currentSkyObjects = useMemo(() => filteredObjects
    .map((object) => {
      const nightWindow = nightWindowForObject(object);
      return { ...object, currentAltitude: currentAltitudeForObject(object), ...nightWindow };
    }), [currentAltitudeForObject, filteredObjects, nightWindowForObject]);

  const availableSkyObjects = useMemo(() => currentSkyObjects
    .filter((object) => object.peakAltitude >= minAltitude), [currentSkyObjects, minAltitude]);

  const visibleSkyObjects = useMemo(() => availableSkyObjects
    .filter((object) => object.currentAltitude >= 0), [availableSkyObjects]);

  const belowHorizonNow = useMemo(() => availableSkyObjects
    .filter((object) => object.currentAltitude < 0), [availableSkyObjects]);

  const belowPlanningAltitude = useMemo(() => currentSkyObjects
    .filter((object) => object.peakAltitude < minAltitude), [currentSkyObjects, minAltitude]);

  const hiddenByHemisphere = useMemo(() => catalogWithVisibility
    .filter((object) => !inHemisphereBand(object, activeHemisphere)), [activeHemisphere, catalogWithVisibility]);

  const moonTelemetry = useMemo(() => {
    const moon = SunCalc.getMoonIllumination(observingDateAtTime(observingDate, timeOffset, observingWindowStartHour));
    return {
      illumination: Math.round(moon.fraction * 100),
      fraction: moon.fraction,
      phaseValue: moon.phase,
      phase: moonPhaseLabel(moon.phase)
    };
  }, [observingDate, observingWindowStartHour, timeOffset]);

  const computeSolarSystemObjects = useCallback((offset = timeOffset) => {
    const date = observingDateAtTime(observingDate, offset, observingWindowStartHour);
    const moonIllumination = SunCalc.getMoonIllumination(date);
    const moonPosition = SunCalc.getMoonPosition(date, viewerLocation.latitude, viewerLocation.longitude);
    const moonLocal = altAzToLocalVector(moonPosition.altitude, moonPosition.azimuth);
    const bodies = [{
      id: 'solar-moon',
      name: 'Moon',
      label: 'Moon',
      kind: 'moon',
      local: moonLocal,
      image: SOLAR_SYSTEM_IMAGES.moon,
      altitude: moonLocal.altitude,
      phase: moonIllumination.phase,
      illumination: moonIllumination.fraction,
      phaseLabel: moonPhaseLabel(moonIllumination.phase),
      magnitude: -12.7,
      description: 'Natural satellite; phase and sky position follow the selected observing date/time/location.'
    }];
    Object.entries(PLANET_STYLES).forEach(([name, style]) => {
      const { ra, dec } = planetRaDec(name, date);
      const local = equatorialToLocalVectorFromRaDec(ra, dec, offset, viewerLocation.latitude, viewerLocation.longitude, observingDate, observingWindowStartHour);
      bodies.push({
        id: `solar-${name.toLowerCase()}`,
        name,
        label: name,
        kind: 'planet',
        style,
        image: SOLAR_SYSTEM_IMAGES[name.toLowerCase()],
        local,
        altitude: local.altitude,
        ra,
        dec,
        magnitude: null,
        description: 'Approximate apparent planet position for planning context; use dedicated ephemeris tools for exact imaging coordinates.'
      });
    });
    const issObject = computeIssSkyObject(date, viewerLocation.latitude, viewerLocation.longitude);
    if (issObject) bodies.push(issObject);
    const selectedComet = REAL_COMETS[selectedCometKey] || REAL_COMETS['12p'];
    bodies.push(computeRealCometObject(selectedComet, offset, viewerLocation.latitude, viewerLocation.longitude, observingDate, observingWindowStartHour));
    return bodies;
  }, [observingDate, observingWindowStartHour, selectedCometKey, timeOffset, viewerLocation.latitude, viewerLocation.longitude]);

  const solarSystemObjects = useMemo(() => computeSolarSystemObjects(timeOffset), [computeSolarSystemObjects, timeOffset]);

  const plannerSkyObjects = useMemo(() => {
    const byId = new Map();
    [...solarSystemObjects, ...Object.values(REAL_COMETS).map((comet) => computeRealCometObject(comet, timeOffset, viewerLocation.latitude, viewerLocation.longitude, observingDate, observingWindowStartHour))]
      .forEach((object) => byId.set(object.id, object));
    return Array.from(byId.values());
  }, [observingDate, observingWindowStartHour, solarSystemObjects, timeOffset, viewerLocation.latitude, viewerLocation.longitude]);

  const visibleSolarSystemObjects = useMemo(() => solarSystemObjects
    .filter((object) => object.altitude >= 0)
    .filter((object) => solarFilters.includes(object.kind)), [solarFilters, solarSystemObjects]);

  const visiblePlanetCount = useMemo(() => visibleSolarSystemObjects
    .filter((object) => object.kind === 'planet').length, [visibleSolarSystemObjects]);

  const visibleIssCount = useMemo(() => visibleSolarSystemObjects
    .filter((object) => object.kind === 'iss').length, [visibleSolarSystemObjects]);

  const visibleCustomCount = useMemo(() => visibleSolarSystemObjects
    .filter((object) => object.kind === 'custom').length, [visibleSolarSystemObjects]);

  const moonVisibleNow = useMemo(() => visibleSolarSystemObjects
    .some((object) => object.kind === 'moon'), [visibleSolarSystemObjects]);

  const selectedSolarObject = selectedId ? solarSystemObjects.find((object) => object.id === selectedId) || null : null;
  const selected = selectedDeepSky || selectedSolarObject;
  const selectedIsSolar = Boolean(selectedSolarObject && !selectedDeepSky);

  const shownTargetCount = visibleSkyObjects.length;
  const belowLineCount = belowPlanningAltitude.length;

  const nightTelemetry = useMemo(() => ([
    ['Location', viewerLocation.label],
    ['Date', observingDateLabel],
    ['Window', observingWindowLabel],
    ['Chart time', displayedTime],
    ['Moon', `${moonTelemetry.illumination}% · ${moonTelemetry.phase}`],
    ['Solar system', `${moonVisibleNow ? 'Moon visible' : 'Moon below horizon'} · ${visiblePlanetCount} planet${visiblePlanetCount === 1 ? '' : 's'} up${solarFilters.includes('iss') ? ` · ISS ${visibleIssCount ? 'up' : 'below'}` : ''}${solarFilters.includes('custom') ? ` · comet ${visibleCustomCount ? 'up' : 'below'}` : ''}`],
    ['Hemisphere', activeHemisphereLabel],
    ['Altitude limit', `${minAltitude}°`],
    ['Targets shown', `${shownTargetCount} visible now`],
    ['Planning limit', `${belowLineCount} never clear ${minAltitude}°`],
    ['Below horizon now', `${belowHorizonNow.length}`],
    ['Guide targets', `${planner.length}`]
  ]), [activeHemisphereLabel, belowHorizonNow.length, belowLineCount, displayedTime, minAltitude, moonTelemetry, moonVisibleNow, observingDateLabel, observingWindowLabel, planner.length, shownTargetCount, solarFilters, viewerLocation.label, visibleCustomCount, visibleIssCount, visiblePlanetCount]);

  const selectedMaxAltitude = selectedDeepSky ? maxAltitudeForLatitude(selectedDeepSky, viewerLocation.latitude) : null;
  const selectedCurrentAltitude = selectedDeepSky ? currentAltitudeForObject(selectedDeepSky) : selectedSolarObject?.altitude ?? null;
  const selectedNightWindow = selectedDeepSky ? nightWindowForObject(selectedDeepSky) : null;
  const selectedInActiveHemisphere = selectedDeepSky ? inHemisphereBand(selectedDeepSky, activeHemisphere) : true;
  const selectedCurrentlyVisible = Boolean(selectedDeepSky
    ? selectedCurrentAltitude >= minAltitude && selectedCurrentAltitude >= 0 && selectedInActiveHemisphere
    : selectedSolarObject && selectedSolarObject.altitude >= 0 && solarFilters.includes(selectedSolarObject.kind));

  const applyScopePreset = (key) => {
    const preset = SCOPE_PRESETS[key] || SCOPE_PRESETS.custom;
    setScopePresetKey(key);
    setOpticalTrain((current) => ({
      ...current,
      focalLength: preset.focalLength,
      aperture: preset.aperture,
      reducer: preset.reducer
    }));
  };

  const applyCameraPreset = (key) => {
    const preset = CAMERA_PRESETS[key] || CAMERA_PRESETS.custom;
    setCameraPresetKey(key);
    setOpticalTrain((current) => ({
      ...current,
      sensorWidth: preset.sensorWidth,
      sensorHeight: preset.sensorHeight,
      pixelSize: preset.pixelSize
    }));
  };

  const updateOpticalValue = (key, value) => {
    setOpticalTrain((current) => ({ ...current, [key]: value }));
    if (['focalLength', 'aperture', 'reducer'].includes(key)) setScopePresetKey('custom');
    if (['sensorWidth', 'sensorHeight', 'pixelSize'].includes(key)) setCameraPresetKey('custom');
  };

  const targetView = useMemo(() => {
    const targetSize = selectedDeepSky ? parseTargetSizeArcMinutes(selectedDeepSky.framingSize || selectedDeepSky.size) : { width: null, height: null, label: 'Deep-sky target required' };
    const baseFocal = safeNumber(opticalTrain.focalLength, 600);
    const reducer = safeNumber(opticalTrain.reducer, 1);
    const effectiveFocal = baseFocal * reducer;
    const aperture = safeNumber(opticalTrain.aperture, 80);
    const sensorWidth = safeNumber(opticalTrain.sensorWidth, 11.31);
    const sensorHeight = safeNumber(opticalTrain.sensorHeight, 11.31);
    const pixelSize = safeNumber(opticalTrain.pixelSize, 3.76);
    const fovWidth = 3437.75 * (sensorWidth / effectiveFocal);
    const fovHeight = 3437.75 * (sensorHeight / effectiveFocal);
    const targetWidthRatio = targetSize.width ? targetSize.width / fovWidth : null;
    const targetHeightRatio = targetSize.height ? targetSize.height / fovHeight : null;
    const fitRatio = Math.max(targetWidthRatio || 0, targetHeightRatio || 0);
    return {
      targetSize,
      effectiveFocal,
      focalRatio: effectiveFocal / aperture,
      fovWidth,
      fovHeight,
      pixelScale: 206.265 * (pixelSize / effectiveFocal),
      targetWidthPercent: targetWidthRatio ? Math.max(8, Math.min(240, targetWidthRatio * 100)) : 42,
      targetHeightPercent: targetHeightRatio ? Math.max(8, Math.min(240, targetHeightRatio * 100)) : 42,
      framing: !selectedDeepSky ? 'Select a deep-sky target to preview imaging scale.'
        : fitRatio > 1.25 ? 'Target is larger than this frame — use a reducer, shorter focal length, or mosaic.'
          : fitRatio > 0.86 ? 'Tight framing — good detail, but check rotation and dithering margins.'
            : fitRatio < 0.18 ? 'Very small in frame — consider more focal length or drizzle/crop.'
              : 'Comfortable framing with useful background margin.'
    };
  }, [opticalTrain, selectedDeepSky]);

  useEffect(() => {
    if (selectedDeepSky && !visibleSkyObjects.some((object) => object.id === selectedId)) {
      setSelectedId(null);
    }
    if (selectedSolarObject && !visibleSolarSystemObjects.some((object) => object.id === selectedId)) {
      setSelectedId(null);
    }
  }, [selectedDeepSky, selectedId, selectedSolarObject, visibleSkyObjects, visibleSolarSystemObjects]);

  useEffect(() => {
    Object.values({ ...TYPE_IMAGE_FALLBACK, ...TARGET_IMAGES, ...SOLAR_SYSTEM_IMAGES }).forEach((src) => {
      if (!src || imageCacheRef.current[src]) return;
      const img = new Image();
      img.onload = () => setImagesReadyTick((tick) => tick + 1);
      img.src = src;
      imageCacheRef.current[src] = img;
    });
  }, []);

  const draw = useCallback(() => {
    // Include image preload ticks so a static canvas repaints as soon as target thumbnails finish loading.
    void imagesReadyTick;
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;
    const rect = wrapper.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    const width = Math.max(320, Math.floor(rect.width));
    const height = Math.max(440, Math.floor(rect.height));
    if (canvas.width !== Math.floor(width * ratio) || canvas.height !== Math.floor(height * ratio)) {
      canvas.width = Math.floor(width * ratio);
      canvas.height = Math.floor(height * ratio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
    }
    const ctx = canvas.getContext('2d');
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const bg = ctx.createRadialGradient(width * 0.56, height * 0.34, 10, width * 0.5, height * 0.48, Math.max(width, height) * 0.72);
    bg.addColorStop(0, '#1d2340');
    bg.addColorStop(0.42, '#0a1020');
    bg.addColorStop(1, '#03040a');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, width, height);

    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    for (let band = 0; band < 3; band += 1) {
      ctx.strokeStyle = `rgba(114,240,255,${0.06 - band * 0.012})`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let i = 0; i <= 240; i += 1) {
        const t = i / 240;
        const x = width * t;
        const y = height * (0.55 + Math.sin(t * Math.PI * 2 + band) * 0.08 + (band - 1) * 0.08);
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
    ctx.restore();

    const sphereRadius = Math.min(width, height) * 0.39 * zoom;
    const sphereCx = width * 0.5;
    const sphereCy = height * 0.5;
    ctx.save();
    ctx.translate(sphereCx, sphereCy);
    const sphereGradient = ctx.createRadialGradient(-sphereRadius * 0.28, -sphereRadius * 0.34, 0, 0, 0, sphereRadius * 1.05);
    sphereGradient.addColorStop(0, 'rgba(38,52,88,0.72)');
    sphereGradient.addColorStop(0.62, 'rgba(10,16,31,0.42)');
    sphereGradient.addColorStop(1, 'rgba(3,5,12,0.02)');
    ctx.fillStyle = sphereGradient;
    ctx.beginPath();
    ctx.arc(0, 0, sphereRadius, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(114,240,255,0.18)';
    ctx.lineWidth = 1.4;
    ctx.stroke();
    ctx.restore();

    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.34)';
    ctx.lineWidth = 2;
    ctx.setLineDash([8, 8]);
    ctx.beginPath();
    let horizonStarted = false;
    let horizonLabel = null;
    for (let i = 0; i <= 240; i += 1) {
      const angle = (i / 240) * Math.PI * 2;
      const point = { x: Math.cos(angle), y: 0, z: Math.sin(angle) };
      const projected = projectSpherePoint(rotatePoint(point, yaw, pitch), width, height, zoom);
      if (!projected.visible) { horizonStarted = false; continue; }
      if (!horizonStarted) { ctx.moveTo(projected.x, projected.y); horizonStarted = true; } else ctx.lineTo(projected.x, projected.y);
      if (i === 30) horizonLabel = projected;
    }
    ctx.stroke();
    ctx.setLineDash([]);
    if (horizonLabel) {
      ctx.fillStyle = 'rgba(255,255,255,0.82)';
      ctx.font = '800 10px Inter, Roboto, sans-serif';
      ctx.fillText('LOCAL HORIZON', horizonLabel.x + 8, horizonLabel.y - 8);
    }
    ctx.restore();

    if (minAltitude >= 0) {
      ctx.save();
      const altitudeRadians = (minAltitude / 180) * Math.PI;
      const altitudeY = Math.sin(altitudeRadians);
      const altitudeRadius = Math.cos(altitudeRadians);
      ctx.strokeStyle = 'rgba(180,124,255,0.36)';
      ctx.lineWidth = 1.6;
      ctx.setLineDash([4, 9]);
      ctx.beginPath();
      let altitudeStarted = false;
      let altitudeLabel = null;
      for (let i = 0; i <= 240; i += 1) {
        const angle = (i / 240) * Math.PI * 2;
        const point = { x: altitudeRadius * Math.cos(angle), y: altitudeY, z: altitudeRadius * Math.sin(angle) };
        const projected = projectSpherePoint(rotatePoint(point, yaw, pitch), width, height, zoom);
        if (!projected.visible) { altitudeStarted = false; continue; }
        if (!altitudeStarted) { ctx.moveTo(projected.x, projected.y); altitudeStarted = true; } else ctx.lineTo(projected.x, projected.y);
        if (i === 36) altitudeLabel = projected;
      }
      ctx.stroke();
      ctx.setLineDash([]);
      if (altitudeLabel) {
        ctx.font = '800 10px Inter, Roboto, sans-serif';
        ctx.fillStyle = 'rgba(214,182,255,0.86)';
        ctx.fillText(`MIN ${minAltitude}° ALT`, altitudeLabel.x + 8, altitudeLabel.y - 8);
      }
      ctx.restore();
    }

    ctx.save();
    ctx.lineCap = 'round';
    let equatorLabel = null;
    let meridianLabel = null;
    for (let dec = -60; dec <= 60; dec += 30) {
      const isEquator = dec === 0;
      ctx.beginPath();
      let started = false;
      for (let i = 0; i <= 220; i += 1) {
        const point = objectVector({ ra: (i / 220) * 24, dec });
        const localPoint = equatorialPointToLocalVector(point, timeOffset, viewerLocation.latitude, viewerLocation.longitude, observingDate, observingWindowStartHour);
        const projected = projectSpherePoint(rotatePoint(localPoint, yaw, pitch), width, height, zoom);
        if (!projected.visible) { started = false; continue; }
        if (!started) { ctx.moveTo(projected.x, projected.y); started = true; } else ctx.lineTo(projected.x, projected.y);
        if (isEquator && i === 150) equatorLabel = projected;
      }
      ctx.strokeStyle = isEquator ? 'rgba(245,151,79,0.82)' : 'rgba(255,255,255,0.075)';
      ctx.lineWidth = isEquator ? 2.6 : 1;
      ctx.setLineDash(isEquator ? [9, 7] : []);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    for (let ra = 0; ra < 24; ra += 3) {
      ctx.beginPath();
      let started = false;
      for (let dec = -82; dec <= 82; dec += 2) {
        const point = objectVector({ ra, dec });
        const localPoint = equatorialPointToLocalVector(point, timeOffset, viewerLocation.latitude, viewerLocation.longitude, observingDate, observingWindowStartHour);
        const projected = projectSpherePoint(rotatePoint(localPoint, yaw, pitch), width, height, zoom);
        if (!projected.visible) { started = false; continue; }
        if (!started) { ctx.moveTo(projected.x, projected.y); started = true; } else ctx.lineTo(projected.x, projected.y);
      }
      ctx.strokeStyle = 'rgba(255,255,255,0.075)';
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    ctx.beginPath();
    let meridianStarted = false;
    for (let i = 0; i <= 240; i += 1) {
      const angle = -Math.PI + (i / 240) * Math.PI * 2;
      const point = { x: 0, y: Math.sin(angle), z: Math.cos(angle) };
      const projected = projectSpherePoint(rotatePoint(point, yaw, pitch), width, height, zoom);
      if (!projected.visible) { meridianStarted = false; continue; }
      if (!meridianStarted) { ctx.moveTo(projected.x, projected.y); meridianStarted = true; } else ctx.lineTo(projected.x, projected.y);
      if (i === 170) meridianLabel = projected;
    }
    ctx.strokeStyle = 'rgba(114,240,255,0.84)';
    ctx.lineWidth = 2.6;
    ctx.setLineDash([5, 5]);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.font = '700 11px Inter, Roboto, sans-serif';
    ctx.textBaseline = 'middle';
    if (equatorLabel) {
      ctx.fillStyle = 'rgba(245,151,79,0.96)';
      ctx.fillText('CELESTIAL EQUATOR', equatorLabel.x + 10, equatorLabel.y - 8);
    }
    if (meridianLabel) {
      ctx.fillStyle = 'rgba(114,240,255,0.95)';
      ctx.fillText('MERIDIAN', meridianLabel.x + 8, meridianLabel.y - 8);
    }
    ctx.restore();

    STAR_FIELD.forEach((star) => {
      const localStar = equatorialPointToLocalVector(star, timeOffset, viewerLocation.latitude, viewerLocation.longitude, observingDate, observingWindowStartHour);
      if (localStar.altitude < 0) return;
      const rotated = rotatePoint(localStar, yaw, pitch);
      const projected = projectSpherePoint(rotated, width, height, zoom);
      if (!projected.visible) return;
      ctx.fillStyle = `rgba(255,255,255,${star.alpha * (0.68 + rotated.z * 0.25)})`;
      ctx.beginPath();
      ctx.arc(projected.x, projected.y, star.size * Math.max(0.38, projected.scale / 240), 0, Math.PI * 2);
      ctx.fill();
    });

    const solarDrawable = visibleSolarSystemObjects
      .map((object) => ({
        object,
        projected: projectSpherePoint(rotatePoint(object.local, yaw, pitch), width, height, zoom)
      }))
      .filter((item) => item.projected.visible)
      .sort((a, b) => a.projected.depth - b.projected.depth);

    solarDrawable.forEach(({ object, projected }) => {
      const scaleBoost = Math.max(0.72, Math.min(1.25, projected.scale / 260));
      const radius = object.kind === 'moon' ? 16 * scaleBoost : object.kind === 'iss' ? 10.5 * scaleBoost : object.kind === 'custom' ? 12.6 * scaleBoost : 11.8 * scaleBoost;
      const isSelected = object.id === selectedId;
      const markerImage = imageCacheRef.current[object.image];
      const drawnImage = drawSolarImageMarker(
        ctx,
        markerImage,
        projected.x,
        projected.y,
        radius,
        object.kind === 'moon' ? '#d9e6ff' : object.style.color,
        object.kind === 'moon' ? { phase: object.phase, illumination: object.illumination } : {}
      );
      if (!drawnImage) {
        if (object.kind === 'moon') {
          drawMoonPhaseMarker(ctx, projected.x, projected.y, radius, object.phase, object.illumination);
        } else if (object.kind === 'custom') {
          ctx.save();
          ctx.shadowColor = object.style.color;
          ctx.shadowBlur = 18;
          ctx.strokeStyle = object.style.color;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(projected.x - radius * 1.8, projected.y + radius * 0.8);
          ctx.quadraticCurveTo(projected.x - radius * 0.5, projected.y, projected.x + radius * 0.9, projected.y - radius * 0.55);
          ctx.stroke();
          drawPlanetMarker(ctx, projected.x + radius * 0.55, projected.y - radius * 0.25, radius * 0.62, object.style.color, object.style.short);
          ctx.restore();
        } else {
          drawPlanetMarker(ctx, projected.x, projected.y, radius, object.style.color, object.style.short);
        }
      }
      if (isSelected) {
        ctx.save();
        ctx.strokeStyle = '#f5974f';
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 5]);
        ctx.beginPath();
        ctx.arc(projected.x, projected.y, radius * 2.35, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }
      ctx.save();
      ctx.font = `800 ${object.kind === 'moon' ? 12 : 10}px Inter, Roboto, sans-serif`;
      ctx.textBaseline = 'middle';
      ctx.fillStyle = object.kind === 'moon' ? 'rgba(235,242,255,0.96)' : rgba(object.style.color, 0.96);
      ctx.shadowColor = 'rgba(0,0,0,0.86)';
      ctx.shadowBlur = 4;
      const suffix = object.kind === 'moon' ? ` ${Math.round(object.illumination * 100)}%` : '';
      ctx.fillText(`${object.label}${suffix}`, projected.x + radius + 7, projected.y - radius * 0.55);
      ctx.restore();
    });

    const trailOffsets = observingWindowMarkOffsets.flatMap((offset, index, source) => (
      index === source.length - 1 ? [offset] : [offset, Number(((offset + source[index + 1]) / 2).toFixed(2))]
    ));
    const trail = selectedDeepSky ? trailOffsets.map((hour) => ({
      hour,
      projected: projectSphereObject(selectedDeepSky, width, height, hour, yaw, pitch, zoom, viewerLocation.latitude, viewerLocation.longitude, observingDate, observingWindowStartHour)
    })).filter((item) => item.projected.visible && item.projected.altitude >= minAltitude) : selectedSolarObject ? trailOffsets.map((hour) => {
      const solarAtHour = computeSolarSystemObjects(hour).find((object) => object.id === selectedSolarObject.id);
      if (!solarAtHour) return null;
      return {
        hour,
        projected: {
          ...projectSpherePoint(rotatePoint(solarAtHour.local, yaw, pitch), width, height, zoom),
          altitude: solarAtHour.altitude
        }
      };
    }).filter(Boolean).filter((item) => item.projected.visible && item.projected.altitude >= 0) : [];
    if (trail.length > 1) {
      ctx.save();
      ctx.strokeStyle = 'rgba(245,151,79,0.48)';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 7]);
      ctx.beginPath();
      trail.forEach(({ projected }, index) => {
        if (index === 0) ctx.moveTo(projected.x, projected.y); else ctx.lineTo(projected.x, projected.y);
      });
      ctx.stroke();
      ctx.setLineDash([]);
      trail.forEach(({ hour, projected }) => {
        const isCurrent = Math.abs(hour - timeOffset) < 0.4;
        ctx.fillStyle = isCurrent ? '#f5974f' : 'rgba(245,151,79,0.62)';
        ctx.beginPath();
        ctx.arc(projected.x, projected.y, isCurrent ? 4.5 : 3, 0, Math.PI * 2);
        ctx.fill();
        if (observingWindowMarkOffsets.some((offset) => Math.abs(offset - hour) < 0.05)) {
          ctx.font = '700 10px Inter, Roboto, sans-serif';
          ctx.fillText(formatEveningTime(hour, observingWindowStartHour), projected.x + 7, projected.y - 7);
        }
      });
      ctx.restore();
    }

    const hits = [];
    solarDrawable.forEach(({ object, projected }) => {
      hits.push({ id: object.id, x: projected.x, y: projected.y, r: object.kind === 'moon' ? 26 : 22, kind: 'solar' });
    });
    const drawable = visibleSkyObjects
      .map((object, index) => ({ object, index, projected: projectSphereObject(object, width, height, timeOffset, yaw, pitch, zoom, viewerLocation.latitude, viewerLocation.longitude, observingDate, observingWindowStartHour) }))
      .filter((item) => item.projected.visible)
      .sort((a, b) => a.projected.depth - b.projected.depth);

    drawable.forEach(({ object, index, projected }) => {
      const style = OBJECT_TYPES[object.type];
      const alpha = Math.max(0.36, Math.min(1, 0.64 + projected.depth * 0.25));
      const radius = Math.max(4.8, Math.min(18, (10.5 - Math.min(object.mag, 10) * 0.38) * (projected.scale / 330)));
      const isSelected = object.id === selectedId;
      ctx.save();
      ctx.shadowColor = style.color;
      ctx.shadowBlur = isSelected ? radius * 2.2 : radius * 0.9;
      const imageSrc = TARGET_IMAGES[object.id] || TYPE_IMAGE_FALLBACK[object.type];
      const targetImage = imageCacheRef.current[imageSrc];
      if (targetImage && targetImage.complete && targetImage.naturalWidth > 0) {
        const imageRadius = isSelected ? radius * 1.85 : radius * 1.28;
        ctx.save();
        ctx.beginPath();
        ctx.arc(projected.x, projected.y, imageRadius, 0, Math.PI * 2);
        ctx.clip();
        const aspect = targetImage.naturalWidth / targetImage.naturalHeight;
        const drawH = imageRadius * 2;
        const drawW = drawH * aspect;
        ctx.drawImage(targetImage, projected.x - drawW / 2, projected.y - drawH / 2, drawW, drawH);
        ctx.restore();
        ctx.strokeStyle = isSelected ? '#f5974f' : rgba(style.color, 0.72);
        ctx.lineWidth = isSelected ? 2 : 0.95;
        ctx.beginPath();
        ctx.arc(projected.x, projected.y, imageRadius, 0, Math.PI * 2);
        ctx.stroke();
      } else {
        if (object.type === 'galaxy') drawGalaxy(ctx, projected.x, projected.y, radius, style.color, alpha, index * 0.47);
        if (object.type === 'nebula') drawNebula(ctx, projected.x, projected.y, radius * 1.22, style.color, alpha, index * 0.61);
        if (object.type === 'cluster') drawCluster(ctx, projected.x, projected.y, radius, style.color, alpha, false, index * 0.51);
        if (object.type === 'globular') drawCluster(ctx, projected.x, projected.y, radius, style.color, alpha, true, index * 0.51);
        if (object.type === 'planetary') drawPlanetary(ctx, projected.x, projected.y, radius * 0.92, style.color, alpha);
      }
      ctx.restore();

      if (isSelected) {
        ctx.strokeStyle = '#f5974f';
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 5]);
        ctx.beginPath();
        ctx.arc(projected.x, projected.y, radius * 2.2, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      if (projected.scale > 92 || isSelected) {
        ctx.font = `${isSelected ? 700 : 600} ${isSelected ? 13 : 11}px Inter, Roboto, sans-serif`;
        ctx.fillStyle = isSelected ? '#fff5e9' : 'rgba(255,255,255,0.76)';
        ctx.fillText(object.catalogue, projected.x + radius * 1.45, projected.y - radius * 0.85);
      }
      hits.push({ id: object.id, x: projected.x, y: projected.y, r: Math.max(12, radius * 2.1) });
    });

    const drawCompassBadge = (label, x, y, accent = '#72f0ff') => {
      ctx.save();
      ctx.font = '900 13px Inter, Roboto, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const badgeW = 34;
      const badgeH = 26;
      ctx.shadowColor = accent;
      ctx.shadowBlur = 14;
      ctx.fillStyle = 'rgba(5,9,20,0.82)';
      ctx.strokeStyle = rgba(accent, 0.72);
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.roundRect(x - badgeW / 2, y - badgeH / 2, badgeW, badgeH, 9);
      ctx.fill();
      ctx.stroke();
      ctx.shadowBlur = 0;
      ctx.fillStyle = accent;
      ctx.fillText(label, x, y + 0.5);
      ctx.restore();
    };
    [
      { label: 'N', point: { x: 0, y: 0, z: 1 }, accent: '#f5974f' },
      { label: 'S', point: { x: 0, y: 0, z: -1 }, accent: '#f5974f' },
      { label: 'E', point: { x: 1, y: 0, z: 0 }, accent: '#72f0ff' },
      { label: 'W', point: { x: -1, y: 0, z: 0 }, accent: '#72f0ff' }
    ].forEach((marker) => {
      const projected = projectSpherePoint(rotatePoint(marker.point, yaw, pitch), width, height, zoom);
      if (!projected.visible) return;
      drawCompassBadge(marker.label, projected.x, projected.y, marker.accent);
    });

    ctx.fillStyle = 'rgba(255,255,255,0.58)';
    ctx.font = '11px Inter, Roboto, sans-serif';
    ctx.fillText('Drag to rotate the sphere · wheel/pinch zoom · click selects · double-click zooms to a real target image', 18, height - 18);
    hitRef.current = hits;
  }, [computeSolarSystemObjects, imagesReadyTick, minAltitude, observingDate, observingWindowMarkOffsets, observingWindowStartHour, pitch, selectedDeepSky, selectedId, selectedSolarObject, timeOffset, viewerLocation.latitude, viewerLocation.longitude, visibleSkyObjects, visibleSolarSystemObjects, yaw, zoom]);

  useEffect(() => {
    draw();
    window.addEventListener('resize', draw);
    return () => window.removeEventListener('resize', draw);
  }, [draw]);

  useEffect(() => {
    if (!autoRotate) return undefined;
    const timer = window.setInterval(() => setYaw((value) => value + 0.0025), 40);
    return () => window.clearInterval(timer);
  }, [autoRotate]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return undefined;
    const handleWheel = (event) => {
      event.preventDefault();
      event.stopPropagation();
      setZoom((value) => Math.max(0.72, Math.min(4.5, value - event.deltaY * 0.001)));
      setAutoRotate(false);
    };
    wrapper.addEventListener('wheel', handleWheel, { passive: false });
    return () => wrapper.removeEventListener('wheel', handleWheel);
  }, []);

  useEffect(() => {
    if (!timeLapsePlaying) return undefined;
    const timer = window.setInterval(() => {
      setTimeOffset((value) => {
        const next = value + 0.08;
        return next > observingWindowDurationHours ? 0 : next;
      });
    }, 160);
    return () => window.clearInterval(timer);
  }, [observingWindowDurationHours, timeLapsePlaying]);

  const hitAt = (clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    return hitRef.current.slice().reverse().find((item) => Math.hypot(item.x - x, item.y - y) <= item.r) || null;
  };

  const selectAt = (clientX, clientY) => {
    const hit = hitAt(clientX, clientY);
    if (hit) {
      setSelectedId(hit.id);
      setAutoRotate(false);
    }
  };

  const focusObject = (objectOrId = selected) => {
    const object = typeof objectOrId === 'string'
      ? SKY_OBJECTS.find((item) => item.id === objectOrId) || solarSystemObjects.find((item) => item.id === objectOrId)
      : objectOrId;
    if (!object) return;
    setSelectedId(object.id);
    setZoom((value) => Math.max(value, 3.2));
    setAutoRotate(false);
  };

  const focusAt = (clientX, clientY) => {
    const hit = hitAt(clientX, clientY);
    if (hit) focusObject(hit.id);
  };

  const resetView = () => {
    setYaw(DEFAULT_VIEW.yaw);
    setPitch(DEFAULT_VIEW.pitch);
    setZoom(DEFAULT_VIEW.zoom);
    setSelectedId(null);
    setTimeOffset(0);
    setTimeLapsePlaying(false);
    setAutoRotate(false);
  };

  const distanceBetweenPointers = (pointers) => {
    if (pointers.length < 2) return 0;
    return Math.hypot(pointers[0].clientX - pointers[1].clientX, pointers[0].clientY - pointers[1].clientY);
  };

  const handleMapPointerDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setAutoRotate(false);
    setTimeLapsePlaying(false);
    if (event.pointerId != null && event.currentTarget?.setPointerCapture) {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch (error) {
        // Some automated/synthetic events and browsers can lack an active pointer for capture.
      }
    }
    if (event.pointerId == null) return;
    activePointersRef.current.set(event.pointerId, { clientX: event.clientX, clientY: event.clientY });
    const pointers = Array.from(activePointersRef.current.values());
    if (pointers.length >= 2) {
      pinchRef.current = { active: true, distance: distanceBetweenPointers(pointers), zoom, moved: false };
      dragRef.current.dragging = false;
      return;
    }
    dragRef.current = { dragging: true, x: event.clientX, y: event.clientY, yaw, pitch };
    pinchRef.current = { active: false, distance: 0, zoom, moved: false };
  };

  const handleMapPointerMove = (event) => {
    if (!activePointersRef.current.has(event.pointerId)) return;
    event.preventDefault();
    event.stopPropagation();
    activePointersRef.current.set(event.pointerId, { clientX: event.clientX, clientY: event.clientY });
    const pointers = Array.from(activePointersRef.current.values());
    if (pointers.length >= 2) {
      const distance = distanceBetweenPointers(pointers);
      if (pinchRef.current.active && pinchRef.current.distance > 0 && distance > 0) {
        const ratio = distance / pinchRef.current.distance;
        if (Math.abs(ratio - 1) > 0.015) pinchRef.current.moved = true;
        setZoom(Math.max(0.72, Math.min(4.5, pinchRef.current.zoom * ratio)));
      }
      dragRef.current.dragging = false;
      return;
    }
    if (!dragRef.current.dragging || pinchRef.current.active) return;
    const dx = event.clientX - dragRef.current.x;
    const dy = event.clientY - dragRef.current.y;
    setYaw(dragRef.current.yaw + dx * 0.006);
    setPitch(Math.max(-1.05, Math.min(1.05, dragRef.current.pitch + dy * 0.004)));
  };

  const handleMapPointerEnd = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const wasPinching = pinchRef.current.active;
    const pinchMoved = pinchRef.current.moved;
    const pointerCountBeforeEnd = activePointersRef.current.size;
    activePointersRef.current.delete(event.pointerId);
    try {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    } catch (error) {
      // Safari/iOS can throw if capture has already been released.
    }
    const remaining = Array.from(activePointersRef.current.values());
    if (remaining.length >= 2) {
      pinchRef.current = { active: true, distance: distanceBetweenPointers(remaining), zoom, moved: pinchMoved };
      dragRef.current.dragging = false;
      return;
    }
    if (remaining.length === 1) {
      pinchRef.current = { active: false, distance: 0, zoom, moved: pinchMoved };
      dragRef.current = { dragging: true, x: remaining[0].clientX, y: remaining[0].clientY, yaw, pitch };
      return;
    }
    if (!wasPinching && !pinchMoved && pointerCountBeforeEnd === 1 && Math.hypot(event.clientX - dragRef.current.x, event.clientY - dragRef.current.y) < 6) {
      selectAt(event.clientX, event.clientY);
    }
    dragRef.current.dragging = false;
    pinchRef.current = { active: false, distance: 0, zoom, moved: false };
  };

  const handleMapPointerCancel = (event) => {
    event.preventDefault();
    event.stopPropagation();
    activePointersRef.current.delete(event.pointerId);
    dragRef.current.dragging = false;
    pinchRef.current = { active: false, distance: 0, zoom, moved: false };
  };

  const displayNameForLocation = useCallback((item) => {
    if (!item) return 'Selected location';
    const baseName = item.name || item.label || 'Selected location';
    if (item.admin1 && !String(baseName).toLowerCase().includes(String(item.admin1).toLowerCase())) {
      return `${baseName}, ${item.admin1}`;
    }
    return baseName;
  }, []);

  const applyPlanningLocation = useCallback((item, options = {}) => {
    if (!item) return;
    const name = displayNameForLocation(item);
    const label = item.label || [name, item.country].filter(Boolean).join(', ');
    const latitude = Number(item.latitude);
    const longitude = Number(item.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      setLocationMessage('That location did not include valid coordinates. Try another town, city or postcode.');
      return;
    }
    pauseMotionForFilterChange();
    setViewerLocation({ label, latitude, longitude });
    setLocationQuery(label);
    const nextHemisphere = hemisphereForLatitude(latitude);
    const shouldExpandSouthernCatalogues = nextHemisphere === 'south'
      && catalogueFilters.length === 1
      && catalogueFilters[0] === 'messier';
    if (shouldExpandSouthernCatalogues) {
      setCatalogueFilters(['messier', 'ngc', 'ic', 'caldwell']);
    }
    if (!options.keepResults) setLocationResults([]);
    setSelectedId(null);
    setLocationMessage(`Planning from ${label} (${latitude.toFixed(2)}°, ${longitude.toFixed(2)}°).${shouldExpandSouthernCatalogues ? ' Southern planning enabled M, NGC, IC and Caldwell targets.' : ''}`);
  }, [catalogueFilters, displayNameForLocation]);

  const searchPlanningLocations = async () => {
    if (!locationQuery.trim()) return;
    setLocationSearching(true);
    setLocationMessage('Searching planning locations…');
    try {
      const encodedQuery = encodeURIComponent(locationQuery.trim());
      const geocodeUrls = Array.from(new Set([
        `/api/night-chart/geocode?q=${encodedQuery}`,
        `${config.endpoints.nightChart}/geocode?q=${encodedQuery}`
      ]));
      let data = null;
      let lastError = null;
      for (const url of geocodeUrls) {
        try {
          const response = await fetch(url);
          const contentType = response.headers.get('content-type') || '';
          if (!response.ok || !contentType.includes('application/json')) {
            throw new Error('Location search endpoint did not return JSON');
          }
          data = await response.json();
          break;
        } catch (error) {
          lastError = error;
        }
      }
      if (!data) throw lastError || new Error('Location search failed');
      const found = data.results || [];
      if (!found.length) {
        setLocationResults([]);
        setLocationMessage('No matching location found. Try a nearby town, city or full postcode.');
        return;
      }
      applyPlanningLocation(found[0], { keepResults: true });
      setLocationResults(found.slice(1, 5));
    } catch (error) {
      setLocationMessage(error.message || 'Unable to search locations.');
    } finally {
      setLocationSearching(false);
    }
  };

  const resetPlanningLocation = () => {
    applyPlanningLocation(DEFAULT_VIEWER_LOCATION);
  };

  const useViewerLocation = () => {
    if (!navigator.geolocation) {
      setLocationMessage('Browser geolocation is unavailable; using the Northumberland default.');
      return;
    }
    setLocationMessage('Requesting viewer location…');
    navigator.geolocation.getCurrentPosition((pos) => {
      const latitude = pos.coords.latitude;
      const longitude = pos.coords.longitude;
      const next = {
        label: `Your location (${latitude.toFixed(2)}°, ${longitude.toFixed(2)}°)`,
        latitude,
        longitude
      };
      applyPlanningLocation(next);
      setLocationResults([]);
      setLocationMessage(`Viewer location active — ${hemisphereName(hemisphereForLatitude(latitude))} mode inferred; opposite-hemisphere and low-altitude targets are filtered.`);
    }, () => {
      setLocationMessage('Location permission was not granted; using Northumberland as the planning default.');
    }, { enableHighAccuracy: false, timeout: 10000, maximumAge: 15 * 60 * 1000 });
  };

  const savePlannerLocal = useCallback((items = planner) => {
    try {
      window.localStorage.setItem(PLANNER_STORAGE_KEY, JSON.stringify(items.map((item) => item.id)));
      setLocationMessage(`Saved ${items.length} target${items.length === 1 ? '' : 's'} to this browser.`);
    } catch (error) {
      setLocationMessage('Could not save the guide in this browser.');
    }
  }, [planner]);

  const isSolarPlannerItem = (item) => ['moon', 'planet', 'iss', 'custom'].includes(item?.kind);
  const plannerItemImage = (item) => (item?.kind === 'custom' ? SOLAR_SYSTEM_IMAGES.comet : isSolarPlannerItem(item) ? item.image || '/logo192.png' : TARGET_IMAGES[item.id] || TYPE_IMAGE_FALLBACK[item.type]);
  const plannerItemTitle = (item) => (isSolarPlannerItem(item)
    ? `${item.name} · ${item.kind === 'iss' ? 'Orbital' : item.kind === 'custom' ? 'Comet' : 'Solar system'}`
    : `${item.catalogue} · ${item.name}`);
  const plannerItemType = (item) => (isSolarPlannerItem(item)
    ? (item.kind === 'moon' ? 'Moon' : item.kind === 'planet' ? 'Planet' : item.kind === 'iss' ? 'ISS / satellite' : 'Real comet')
    : OBJECT_TYPES[item.type].label);
  const plannerItemMeta = (item) => (isSolarPlannerItem(item)
    ? `${plannerItemType(item)} · ${Math.round(item.altitude)}° altitude · ${displayedTime} · ${observingDateLabel}`
    : `${item.constellation} · ${OBJECT_TYPES[item.type].label} · Mag ${item.mag} · ${item.size}`);
  const plannerItemPlanning = (item) => (isSolarPlannerItem(item)
    ? `${observingWindowLabel} trajectory · ${viewerLocation.label}`
    : `Best ${item.season} · ${item.focal} · ${item.exposure}`);
  const plannerItemNotes = (item) => (isSolarPlannerItem(item)
    ? `${item.description || 'Sky object.'} ${item.kind === 'moon' ? `Phase: ${Math.round(item.illumination * 100)}% · ${item.phaseLabel}.` : `Position: RA ${item.ra.toFixed(2)}h / Dec ${item.dec.toFixed(1)}°${item.kind === 'iss' && item.rangeKm ? ` · range ${item.rangeKm} km` : ''}.`} Use the selected date/time/location for altitude and trajectory.`
    : item.guide);
  const solarCoordinateSummary = (item) => {
    if (!item) return '—';
    if (Number.isFinite(Number(item.ra)) && Number.isFinite(Number(item.dec))) {
      return `RA ${Number(item.ra).toFixed(2)}h / Dec ${Number(item.dec).toFixed(1)}°`;
    }
    return item.kind === 'moon'
      ? `Local lunar position · ${Math.round(item.altitude)}° altitude`
      : `Local sky position · ${Math.round(item.altitude)}° altitude`;
  };

  const addToPlanner = (object = selected) => {
    if (!object) return;
    const next = planner.some((item) => item.id === object.id) ? planner : [...planner, object];
    setPlanner(next);
    savePlannerLocal(next);
  };

  const removeFromPlanner = (id) => {
    const next = planner.filter((item) => item.id !== id);
    setPlanner(next);
    savePlannerLocal(next);
  };

  const exportPlanner = () => {
    const payload = {
      title: 'Astro Vino night planning guide',
      savedAt: new Date().toISOString(),
      observingDate,
      observingDateLabel,
      viewerLocation,
      targets: planner.map((item, index) => ({
        order: index + 1,
        catalogue: isSolarPlannerItem(item) ? 'Solar system' : item.catalogue,
        name: item.name,
        constellation: isSolarPlannerItem(item) ? 'Solar system' : item.constellation,
        type: plannerItemType(item),
        ra: item.ra,
        dec: item.dec,
        magnitude: isSolarPlannerItem(item) ? null : item.mag,
        apparentSize: isSolarPlannerItem(item) ? null : item.size,
        bestSeason: isSolarPlannerItem(item) ? observingDateLabel : item.season,
        focalLength: isSolarPlannerItem(item) ? 'Use object-specific lunar/planetary framing' : item.focal,
        integration: isSolarPlannerItem(item) ? 'Short exposures/video capture; stack lucky-imaging frames where appropriate' : item.exposure,
        altitude: isSolarPlannerItem(item) ? item.altitude : currentAltitudeForObject(item),
        chartTime: displayedTime,
        trajectoryWindow: observingWindowLabel,
        notes: plannerItemNotes(item)
      }))
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `astro-vino-night-guide-${observingDate}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportPlannerDocument = () => {
    const savedDate = new Date().toLocaleString();
    const escapeHtml = (value) => String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
    const targetCards = planner.map((item, index) => {
      const thumbnail = `${window.location.origin}${plannerItemImage(item)}`;
      return `
        <article class="target-card">
          <img src="${escapeHtml(thumbnail)}" alt="${escapeHtml(item.name)} target thumbnail" />
          <div>
            <h2>${index + 1}. ${escapeHtml(plannerItemTitle(item))}</h2>
            <dl>
              <div><dt>Type</dt><dd>${escapeHtml(plannerItemType(item))}</dd></div>
              <div><dt>Context</dt><dd>${escapeHtml(isSolarPlannerItem(item) ? viewerLocation.label : item.constellation)}</dd></div>
              <div><dt>RA / Dec</dt><dd>${escapeHtml(formatRa(item.ra))} / ${escapeHtml(formatDec(item.dec))}</dd></div>
              <div><dt>${escapeHtml(isSolarPlannerItem(item) ? 'Chart time' : 'Magnitude')}</dt><dd>${escapeHtml(isSolarPlannerItem(item) ? displayedTime : item.mag)}</dd></div>
              <div><dt>${escapeHtml(isSolarPlannerItem(item) ? 'Altitude' : 'Size')}</dt><dd>${escapeHtml(isSolarPlannerItem(item) ? `${Math.round(item.altitude)}°` : item.size)}</dd></div>
              <div><dt>${escapeHtml(isSolarPlannerItem(item) ? 'Date' : 'Best season')}</dt><dd>${escapeHtml(isSolarPlannerItem(item) ? observingDateLabel : item.season)}</dd></div>
              <div><dt>Framing</dt><dd>${escapeHtml(isSolarPlannerItem(item) ? 'Lunar/planetary framing' : item.focal)}</dd></div>
              <div><dt>Capture</dt><dd>${escapeHtml(isSolarPlannerItem(item) ? 'Short exposures / video stack' : item.exposure)}</dd></div>
            </dl>
            <p>${escapeHtml(plannerItemNotes(item))}</p>
          </div>
        </article>`;
    }).join('');
    const telemetryCards = nightTelemetry.map(([label, value]) => `
      <div class="telemetry-card"><span>${escapeHtml(label)}</span><strong>${escapeHtml(value)}</strong></div>
    `).join('');

    const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Astro Vino Night Planning Guide</title>
  <style>
    @page { margin: 14mm; }
    * { box-sizing: border-box; }
    body { font-family: Inter, Roboto, Arial, sans-serif; color: #102033; margin: 0; background: #f7faff; }
    .page { max-width: 1040px; margin: 0 auto; padding: 30px 34px 48px; background: #fff; min-height: 100vh; }
    header { border-bottom: 3px solid #f5974f; padding-bottom: 14px; margin-bottom: 18px; }
    .eyebrow { color: #0b7e90; font-size: 11px; font-weight: 800; letter-spacing: 1.8px; text-transform: uppercase; }
    h1 { margin: 6px 0 8px; font-size: 30px; line-height: 1.05; }
    .meta { color: #526173; font-size: 12px; line-height: 1.45; }
    .telemetry { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin: 0 0 18px; }
    .telemetry-card { border: 1px solid #d9e2ee; border-radius: 12px; padding: 9px 10px; background: #f8fbff; }
    .telemetry-card span { display: block; color: #66768a; font-size: 9px; font-weight: 900; letter-spacing: 0.9px; text-transform: uppercase; }
    .telemetry-card strong { display: block; margin-top: 3px; font-size: 12px; color: #102033; }
    .target-card { break-inside: avoid; page-break-inside: avoid; display: grid; grid-template-columns: 150px minmax(0, 1fr); gap: 16px; border: 1px solid #d9e2ee; border-radius: 14px; padding: 14px; margin: 0 0 14px; }
    .target-card img { width: 150px; height: 116px; object-fit: cover; border-radius: 10px; background: #071020; }
    h2 { margin: 0 0 9px; font-size: 18px; color: #071020; }
    dl { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px 12px; margin: 0 0 10px; }
    dt { color: #66768a; font-size: 10px; font-weight: 800; letter-spacing: 0.8px; text-transform: uppercase; }
    dd { margin: 2px 0 0; font-size: 12px; font-weight: 700; color: #102033; }
    p { margin: 0; color: #33455b; font-size: 12px; line-height: 1.45; }
    .checklist { margin-top: 18px; padding: 12px 16px; border-radius: 14px; background: #f3f7fb; }
    .checklist h2 { font-size: 16px; margin-bottom: 8px; }
    .checklist li { margin: 5px 0; font-size: 12px; }
    @media (max-width: 760px) { .page { padding: 22px 18px 34px; } .telemetry { grid-template-columns: repeat(2, minmax(0, 1fr)); } .target-card { grid-template-columns: 1fr; } .target-card img { width: 100%; height: 180px; } dl { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
    @media print { body { background: #fff; print-color-adjust: exact; -webkit-print-color-adjust: exact; } .page { max-width: none; padding: 0; min-height: auto; } }
  </style>
</head>
<body>
<main class="page">
  <header>
    <div class="eyebrow">Astro Vino</div>
    <h1>Night Planning Guide</h1>
    <div class="meta">
      Saved: ${escapeHtml(savedDate)}<br />
      Viewer location: ${escapeHtml(viewerLocation.label)} (${viewerLocation.latitude.toFixed(2)}°, ${viewerLocation.longitude.toFixed(2)}°)<br />
      Observing date: ${escapeHtml(observingDateLabel)}<br />
      Minimum target altitude: ${minAltitude}°
    </div>
  </header>
  <section class="telemetry" aria-label="Night telemetry">
    ${telemetryCards}
  </section>
  ${targetCards}
  <section class="checklist">
    <h2>Session checklist</h2>
    <ul>
      <li>Confirm target order against sky position and meridian timing.</li>
      <li>Check Moon separation / phase tolerance.</li>
      <li>Set framing and focal length before capture.</li>
      <li>Confirm filters, exposure length, gain/ISO, dithering and total integration.</li>
      <li>Save calibration frames and target notes with the final image stack.</li>
    </ul>
  </section>
</main>
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const pdfWindow = window.open(url, '_blank');
    if (!pdfWindow) {
      URL.revokeObjectURL(url);
      setLocationMessage('Could not open the PDF document window. Allow pop-ups for this site, then try Save Document again.');
      return;
    }
    window.setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
  };

  useEffect(() => {
    try {
      const stored = JSON.parse(window.localStorage.getItem(PLANNER_STORAGE_KEY) || '[]');
      const restored = stored
        .map((id) => SKY_OBJECTS.find((object) => object.id === id) || plannerSkyObjects.find((object) => object.id === id))
        .filter(Boolean);
      if (restored.length) setPlanner(restored);
    } catch (error) {
      // Ignore malformed local planner data.
    }
  }, [plannerSkyObjects]);

  return (
    <Box sx={{ minHeight: '100vh', pb: 6, background: 'radial-gradient(circle at 18% 5%, rgba(128,215,255,0.16), transparent 32%), radial-gradient(circle at 82% 10%, rgba(245,151,79,0.16), transparent 32%), #050711' }}>
      <Box sx={{ py: { xs: 1.25, md: 1.6 }, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Container maxWidth="xl">
          <Stack spacing={0.55} sx={{ minWidth: 0 }}>
            <Chip icon={<ExploreIcon />} label="Interactive Astro Photography Atlas" size="small" sx={{ alignSelf: 'flex-start', bgcolor: 'rgba(245,151,79,0.14)', color: 'primary.light', border: '1px solid rgba(245,151,79,0.35)', fontWeight: 800, height: 24 }} />
            <Typography variant="h1" sx={{ fontSize: { xs: '1.32rem', md: '1.82rem' }, lineHeight: 1.18, letterSpacing: '-0.03em', fontWeight: 900, overflow: 'visible' }}>
              Deep-sky sphere + night-planning guide
            </Typography>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 1.35 }}>
        <Grid container spacing={1.4}>
          <Grid item xs={12} lg={8.7}>
            <Paper sx={{ p: { xs: 0.8, md: 1 }, background: 'linear-gradient(160deg, rgba(13,18,36,0.96), rgba(4,5,12,0.96))', border: '1px solid rgba(128,215,255,0.18)', boxShadow: '0 30px 90px rgba(0,0,0,0.42)', overflow: 'hidden' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={0.8} alignItems={{ md: 'center' }} justifyContent="space-between" sx={{ mb: 0.75 }}>
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.55, '& .MuiChip-root': { height: 24, fontSize: '0.68rem' } }}>
                  <Chip icon={<PublicIcon />} label="Static deep-sky sphere" size="small" />
                  <Chip icon={<AutoAwesomeIcon />} label={`${shownTargetCount} shown · ${belowLineCount} below line · ${hiddenByHemisphere.length} hemisphere`} size="small" sx={{ color: '#72f0ff', borderColor: 'rgba(114,240,255,0.35)' }} variant="outlined" />
                  <Chip label={`Zoom ${zoom.toFixed(1)}×`} size="small" />
                  <Chip icon={<CalendarMonthIcon />} label={observingDateLabel} size="small" />
                  <Chip label={`${moonVisibleNow ? `Moon ${moonTelemetry.illumination}%` : 'Moon below'} · ${visiblePlanetCount} planet${visiblePlanetCount === 1 ? '' : 's'}`} size="small" sx={{ color: '#dbe7ff', borderColor: 'rgba(219,231,255,0.32)' }} variant="outlined" />
                  <Chip icon={<LocationOnIcon />} label={`${activeHemisphereLabel} · ${viewerLocation.label} · min ${minAltitude}°`} size="small" />
                </Stack>
                <Stack direction="row" spacing={0.6} sx={{ '& .MuiButton-root': { minHeight: 30, px: 1.05, fontSize: '0.68rem' } }}>
                  <Button size="small" variant={autoRotate ? 'contained' : 'outlined'} onClick={() => setAutoRotate((value) => !value)}>
                    {autoRotate ? 'Orbiting' : 'Orbit'}
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<CenterFocusStrongIcon />} disabled={!selected} onClick={() => focusObject()}>Zoom target</Button>
                  <Button size="small" variant="outlined" startIcon={<RestartAltIcon />} onClick={resetView}>Reset start</Button>
                </Stack>
              </Stack>

              <Paper sx={{ mb: 1.05, p: { xs: 0.9, md: 1.05 }, background: 'linear-gradient(180deg, rgba(12,17,34,0.86), rgba(5,8,18,0.92))', border: '1px solid rgba(245,151,79,0.26)', borderRadius: 1.4, overflow: 'hidden' }}>
                <Stack spacing={0.65}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, flexWrap: 'wrap', rowGap: 0.45, '& .MuiChip-root': { height: 24, fontSize: '0.68rem' } }}>
                      <Chip label="Equatorial time-lapse" size="small" sx={{ bgcolor: 'rgba(245,151,79,0.16)', color: 'primary.light', fontWeight: 800 }} />
                      <Box sx={{ px: 0.9, py: 0.35, borderRadius: 1, minWidth: 58, textAlign: 'center', bgcolor: 'rgba(245,151,79,0.10)', border: '1px solid rgba(245,151,79,0.30)', color: '#fff5e9', fontWeight: 900, fontSize: '0.86rem', letterSpacing: 0.2 }}>
                        {displayedTime}
                      </Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.60)', display: { xs: 'none', sm: 'inline' } }}>{observingWindowLabel}</Typography>
                      <Stack direction="row" spacing={0.45} alignItems="center" sx={{ '& .MuiTextField-root': { width: 82 }, '& .MuiInputBase-root': { height: 27, fontSize: '0.72rem', bgcolor: 'rgba(255,255,255,0.045)' }, '& .MuiInputBase-input': { py: 0.2, color: '#fff5e9' }, '& fieldset': { borderColor: 'rgba(245,151,79,0.34) !important' } }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.52)', fontSize: '0.64rem' }}>Start</Typography>
                        <TextField select size="small" value={observingWindowStartHour} onChange={(event) => updateObservingWindowStart(event.target.value)} SelectProps={{ native: true }}>
                          {OBSERVING_WINDOW_HOUR_OPTIONS.map((hour) => <option key={hour} value={hour}>{formatClockHour(hour)}</option>)}
                        </TextField>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.52)', fontSize: '0.64rem' }}>End</Typography>
                        <TextField select size="small" value={observingWindowEndHour} onChange={(event) => updateObservingWindowEnd(event.target.value)} SelectProps={{ native: true }}>
                          {OBSERVING_WINDOW_HOUR_OPTIONS.map((hour) => <option key={hour} value={hour}>{formatClockHour(hour)}</option>)}
                        </TextField>
                      </Stack>
                    </Stack>
                    <IconButton
                      size="small"
                      aria-label={timeLapsePlaying ? 'Pause equatorial time-lapse' : 'Play equatorial time-lapse'}
                      title={timeLapsePlaying ? 'Pause time-lapse' : 'Play time-lapse'}
                      onClick={() => { setTimeLapsePlaying((value) => !value); setAutoRotate(false); }}
                      sx={{
                        width: 32,
                        height: 30,
                        flexShrink: 0,
                        color: timeLapsePlaying ? '#160920' : '#f5974f',
                        bgcolor: timeLapsePlaying ? 'primary.main' : 'rgba(245,151,79,0.07)',
                        border: '1px solid rgba(245,151,79,0.52)',
                        borderRadius: 1.1,
                        '&:hover': { bgcolor: timeLapsePlaying ? 'primary.light' : 'rgba(245,151,79,0.15)' }
                      }}
                    >
                      {timeLapsePlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                    </IconButton>
                  </Stack>

                  <Box sx={{ px: { xs: 0.65, sm: 0.95, md: 1.25 }, pt: 0.35, pb: 0.2 }}>
                    <Slider
                      size="small"
                      min={0}
                      max={observingWindowDurationHours}
                      step={0.25}
                      value={timeOffset}
                      onChange={(_, value) => { setTimeOffset(value); setTimeLapsePlaying(false); setAutoRotate(false); }}
                      marks={false}
                      sx={{
                        width: '100%',
                        display: 'block',
                        py: 0.55,
                        color: '#f5974f',
                        '& .MuiSlider-rail': { height: 3, opacity: 1, bgcolor: 'rgba(255,255,255,0.14)' },
                        '& .MuiSlider-track': { height: 3, border: 0, background: 'linear-gradient(90deg, rgba(245,151,79,0.95), rgba(255,202,128,0.88))' },
                        '& .MuiSlider-thumb': {
                          width: 12,
                          height: 12,
                          bgcolor: '#f7a15b',
                          border: '2px solid #1b1020',
                          boxShadow: '0 0 0 4px rgba(245,151,79,0.14), 0 0 20px rgba(245,151,79,0.65)',
                          '&:hover, &.Mui-focusVisible': { boxShadow: '0 0 0 6px rgba(245,151,79,0.20), 0 0 20px rgba(245,151,79,0.65)' },
                          '&:before': { display: 'none' }
                        }
                      }}
                    />
                    <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${observingWindowMarkOffsets.length}, 1fr)`, mt: 0.1 }}>
                      {observingWindowMarkOffsets.map((value, index) => (
                        <Typography
                          key={value}
                          component="span"
                          sx={{
                            fontSize: '0.64rem',
                            lineHeight: 1,
                            color: 'rgba(255,255,255,0.70)',
                            textAlign: index === 0 ? 'left' : index === observingWindowMarkOffsets.length - 1 ? 'right' : 'center',
                            whiteSpace: 'nowrap',
                            fontVariantNumeric: 'tabular-nums'
                          }}
                        >
                          {formatEveningTime(value, observingWindowStartHour)}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                </Stack>
              </Paper>

              <Box
                ref={wrapperRef}
                sx={{ height: { xs: 560, md: 720 }, position: 'relative', borderRadius: 2.2, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)', cursor: dragRef.current.dragging ? 'grabbing' : 'grab', overscrollBehavior: 'contain', touchAction: 'none' }}
                onPointerDown={handleMapPointerDown}
                onPointerMove={handleMapPointerMove}
                onPointerUp={handleMapPointerEnd}
                onPointerCancel={handleMapPointerCancel}
                onLostPointerCapture={handleMapPointerCancel}
                onDoubleClick={(event) => focusAt(event.clientX, event.clientY)}
              >
                <canvas ref={canvasRef} aria-label="Interactive 3D deep sky object map" style={{ display: 'block', touchAction: 'none' }} />
                {selected && (
                  <Box sx={{ position: 'absolute', top: 12, left: 12, right: { xs: 12, md: 'auto' }, maxWidth: 330, p: 0.9, borderRadius: 1.6, background: 'rgba(3,5,12,0.58)', border: '1px solid rgba(255,255,255,0.09)', backdropFilter: 'blur(12px)' }}>
                    <Typography variant="caption" sx={{ color: '#72f0ff', letterSpacing: 1.2, textTransform: 'uppercase', fontWeight: 800, fontSize: '0.66rem' }}>{selectedIsSolar ? 'Selected solar-system object' : 'Selected target'}</Typography>
                    <Typography sx={{ fontSize: { xs: '0.98rem', md: '1.08rem' }, fontWeight: 900, lineHeight: 1.05 }}>{selectedIsSolar ? selected.name : `${selected.catalogue} · ${selected.name}`}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedIsSolar
                        ? `${selected.kind === 'moon' ? `${Math.round(selected.illumination * 100)}% · ${selected.phaseLabel}` : 'Planet'} · now ${Math.round(selected.altitude)}° alt · ${displayedTime}`
                        : `${selected.constellation} · Mag ${selected.mag} · now ${Math.round(selectedCurrentAltitude)}° alt · max ${Math.round(selectedMaxAltitude)}° · ${displayedTime}`}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>

            <Paper sx={{ mt: 2.2, p: { xs: 1.3, md: 1.8 }, background: 'rgba(10,12,22,0.94)', border: '1px solid rgba(118,255,220,0.15)' }}>
              <Stack spacing={1.4}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} justifyContent="space-between">
                  <Stack direction="row" spacing={1} alignItems="center"><CameraAltIcon sx={{ color: '#76ffdc' }} /><Typography sx={{ fontWeight: 900 }}>Night planning guide</Typography></Stack>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    <Button size="small" variant="outlined" disabled={!planner.length} onClick={() => savePlannerLocal()}>Save Browser</Button>
                    <Button size="small" variant="outlined" disabled={!planner.length} onClick={exportPlannerDocument}>Save Document</Button>
                    <Button size="small" variant="outlined" disabled={!planner.length} onClick={exportPlanner}>Export JSON</Button>
                  </Stack>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Select targets on the sphere, add them to the guide, then save in this browser, open a document guide, or export structured JSON. The document page can be printed or saved as PDF from the browser.
                </Typography>
                <Box sx={{ p: 1.15, borderRadius: 2, background: 'linear-gradient(135deg, rgba(118,255,220,0.08), rgba(128,215,255,0.06))', border: '1px solid rgba(118,255,220,0.16)' }}>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.8, color: '#76ffdc', letterSpacing: 1.1, textTransform: 'uppercase', fontWeight: 900 }}>Night telemetry</Typography>
                  <Grid container spacing={0.8}>
                    {nightTelemetry.map(([label, value]) => (
                      <Grid item xs={6} sm={4} md={3} key={label}>
                        <Box sx={{ p: 0.8, borderRadius: 1.4, bgcolor: 'rgba(255,255,255,0.045)', border: '1px solid rgba(255,255,255,0.08)', minHeight: 54 }}>
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.62rem', fontWeight: 850 }}>{label}</Typography>
                          <Typography variant="caption" sx={{ color: '#f7fbff', fontWeight: 850, lineHeight: 1.25 }}>{value}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
                {planner.length === 0 ? (
                  <Box sx={{ p: 1.6, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.04)', border: '1px dashed rgba(118,255,220,0.22)' }}>
                    <Typography variant="body2" color="text.secondary">No targets yet — click a target on the 3D map to reveal its details, then add it to this local planning guide.</Typography>
                  </Box>
                ) : (
                  <Stack spacing={1.1}>
                    {planner.map((item, index) => (
                      <Paper key={item.id} sx={{ p: 1.1, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <Grid container spacing={1.2} alignItems="center">
                          <Grid item xs={12} sm={3} md={2.2}>
                            <Box component="img" src={plannerItemImage(item)} alt={`${item.name} planning thumbnail`} sx={{ width: '100%', height: 92, objectFit: item.kind === 'custom' ? 'cover' : isSolarPlannerItem(item) ? 'contain' : 'cover', borderRadius: 1.6, border: '1px solid rgba(255,255,255,0.12)', bgcolor: isSolarPlannerItem(item) ? 'radial-gradient(circle at 50% 45%, rgba(255,238,180,0.10), rgba(0,0,0,0.78) 62%)' : 'rgba(0,0,0,0.35)', p: item.kind === 'custom' ? 0 : undefined }} />
                          </Grid>
                          <Grid item xs={12} sm={7.5} md={8.6}>
                            <Stack spacing={0.45}>
                              <Typography sx={{ fontWeight: 900 }}>{index + 1}. {plannerItemTitle(item)}</Typography>
                              <Typography variant="body2" color="text.secondary">{plannerItemMeta(item)}</Typography>
                              <Typography variant="caption" color="text.secondary">{plannerItemPlanning(item)}</Typography>
                              <Typography variant="caption" sx={{ color: '#dce8ff' }}>{plannerItemNotes(item)}</Typography>
                            </Stack>
                          </Grid>
                          <Grid item xs={12} sm={1.5} md={1.2} sx={{ textAlign: { sm: 'right' } }}>
                            <IconButton size="small" aria-label={`Remove ${item.name} from guide`} onClick={() => removeFromPlanner(item.id)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                    <LinearProgress variant="determinate" value={Math.min(100, planner.length * 20)} sx={{ height: 7, borderRadius: 999 }} />
                    <Typography variant="caption" color="text.secondary">Guide checklist: target order · framing/focal length · filters · moon tolerance · integration time.</Typography>
                  </Stack>
                )}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={3.3}>
            <Stack spacing={1.25}>
              {selected ? (
                <Paper sx={{ p: 1.45, background: 'linear-gradient(155deg, rgba(245,151,79,0.13), rgba(10,12,22,0.96))', border: '1px solid rgba(245,151,79,0.28)', boxShadow: '0 18px 50px rgba(0,0,0,0.26)' }}>
                  <Stack spacing={1.05}>
                    <Chip label={selectedIsSolar ? `${plannerItemType(selected)} · ${selected.kind === 'iss' ? 'Orbital' : selected.kind === 'custom' ? 'Comet' : 'Solar system'}` : `${OBJECT_TYPES[selected.type].label} · ${catalogueSummaryLabel(selected)}`} sx={{ alignSelf: 'flex-start', bgcolor: selectedIsSolar ? 'rgba(219,231,255,0.16)' : OBJECT_TYPES[selected.type].glow, color: selectedIsSolar ? '#dbe7ff' : OBJECT_TYPES[selected.type].color, fontWeight: 800 }} />
                    <Box component="img" src={plannerItemImage(selected)} alt={`${selected.name} planning reference`} sx={{ width: '100%', height: selectedIsSolar ? 150 : 150, objectFit: selected.kind === 'custom' ? 'cover' : selectedIsSolar ? 'contain' : 'cover', borderRadius: 1.7, border: '1px solid rgba(255,255,255,0.12)', bgcolor: selectedIsSolar ? 'radial-gradient(circle at 50% 45%, rgba(255,238,180,0.12), rgba(4,5,12,0.98) 58%)' : 'rgba(0,0,0,0.35)', p: selectedIsSolar && selected.kind !== 'custom' ? 0.65 : 0 }} />
                    <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.1 }}>{selected.name}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.48 }}>{selectedIsSolar ? selected.description : selected.guide}</Typography>
                    <Box sx={{ p: 0.9, borderRadius: 1.35, bgcolor: selectedCurrentlyVisible ? 'rgba(118,255,178,0.12)' : 'rgba(255,107,138,0.14)', border: '1px solid rgba(255,255,255,0.10)' }}>
                      <Typography variant="caption" sx={{ color: selectedCurrentlyVisible ? '#76ffb2' : '#ff8aa3', fontWeight: 850 }}>
                        {selectedIsSolar
                          ? `${selected.name} is ${selectedCurrentAltitude >= 0 ? 'above' : 'below'} the local horizon from ${viewerLocation.label}: currently ${Math.round(selectedCurrentAltitude)}° altitude at ${displayedTime}. The dashed trail shows its ${observingWindowLabel} movement.`
                          : selectedNightWindow?.peakAltitude < minAltitude
                            ? `Never clears the ${minAltitude}° altitude line during ${observingWindowLabel} from ${viewerLocation.label}: currently ${Math.round(selectedCurrentAltitude)}°, peaks around ${Math.round(selectedNightWindow.peakAltitude)}°.`
                            : selectedCurrentAltitude < 0
                              ? `Below the local horizon now from ${viewerLocation.label}: currently ${Math.round(selectedCurrentAltitude)}°, but clears the ${minAltitude}° line around ${formatEveningTime(selectedNightWindow?.firstOffsetAboveLimit ?? 0, observingWindowStartHour)}.`
                              : `${selectedCurrentAltitude >= minAltitude ? 'Above' : 'Below now, but clears'} the ${minAltitude}° altitude line during the night from ${viewerLocation.label}: currently ${Math.round(selectedCurrentAltitude)}°, peaks around ${Math.round(selectedNightWindow?.peakAltitude ?? selectedMaxAltitude)}°${selectedNightWindow?.firstOffsetAboveLimit != null ? ` around ${formatEveningTime(selectedNightWindow.firstOffsetAboveLimit, observingWindowStartHour)}` : ''}.`}
                      </Typography>
                    </Box>
                    <Grid container spacing={0.8}>
                      {(selectedIsSolar
                        ? [['Object', selected.name], ['Type', plannerItemType(selected)], ['Altitude now', `${Math.round(selectedCurrentAltitude)}°`], ['Chart time', displayedTime], ['Position', solarCoordinateSummary(selected)], ['Trajectory', observingWindowLabel]]
                        : [['Catalogue', selected.catalogue], ['Constellation', selected.constellation], ['RA / Dec', `${selected.ra.toFixed(2)}h / ${selected.dec.toFixed(1)}°`], ['Mag', selected.mag], ['Best season', selected.season], ['Integration', selected.exposure]])
                        .map(([label, value]) => (
                        <Grid item xs={6} key={label}>
                          <Box sx={{ p: 0.75, borderRadius: 1.25, bgcolor: 'rgba(255,255,255,0.045)', border: '1px solid rgba(255,255,255,0.07)', minHeight: 54 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.62rem' }}>{label}</Typography>
                            <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', lineHeight: 1.25 }}>{value}</Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.10)' }} />
                    <Box sx={{ borderRadius: 1.6, border: '1px solid rgba(118,240,255,0.20)', bgcolor: 'rgba(118,240,255,0.045)', overflow: 'hidden' }}>
                      <Button
                        fullWidth
                        variant="text"
                        onClick={() => setTargetViewOpen((value) => !value)}
                        startIcon={<CameraAltIcon />}
                        sx={{
                          justifyContent: 'space-between',
                          px: 1,
                          py: 0.8,
                          color: '#76ffdc',
                          fontWeight: 900,
                          '& .MuiButton-startIcon': { mr: 0.8 },
                          '&:hover': { bgcolor: 'rgba(118,240,255,0.08)' }
                        }}
                      >
                        <Box component="span" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.12 }}>
                          <Box component="span">Target view / imaging FOV</Box>
                          <Box component="span" sx={{ color: 'rgba(220,232,255,0.62)', fontSize: '0.64rem', fontWeight: 750 }}>
                            {targetViewOpen ? 'Hide telescope + camera framing controls' : 'Collapsed · preview focal length, aperture, reducer/barlow and camera'}
                          </Box>
                        </Box>
                        <Box component="span" sx={{ color: '#f5974f', fontSize: '0.78rem', fontWeight: 900 }}>{targetViewOpen ? 'Collapse' : 'Open'}</Box>
                      </Button>
                      <Collapse in={targetViewOpen} timeout="auto" unmountOnExit>
                        <Stack spacing={1.05} sx={{ p: 1, pt: 0.2 }}>
                          {!selectedDeepSky && (
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35 }}>
                              Target view is built for deep-sky catalogue objects. Select a DSO on the sphere to scale the object against your imaging train.
                            </Typography>
                          )}
                          <Grid container spacing={0.75}>
                            <Grid item xs={12}>
                              <TextField select fullWidth size="small" label="Telescope / optical tube" value={scopePresetKey} onChange={(event) => applyScopePreset(event.target.value)} SelectProps={{ native: true }}>
                                {SCOPE_PRESET_KEYS.map((key) => <option key={key} value={key}>{SCOPE_PRESETS[key].label}</option>)}
                              </TextField>
                            </Grid>
                            <Grid item xs={6}>
                              <TextField fullWidth size="small" label="Focal length (mm)" type="number" value={opticalTrain.focalLength} onChange={(event) => updateOpticalValue('focalLength', event.target.value)} onInput={(event) => updateOpticalValue('focalLength', event.currentTarget.value)} inputProps={{ min: 50, step: 1, 'aria-label': 'Telescope focal length in millimetres' }} />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField fullWidth size="small" label="Aperture (mm)" type="number" value={opticalTrain.aperture} onChange={(event) => updateOpticalValue('aperture', event.target.value)} onInput={(event) => updateOpticalValue('aperture', event.currentTarget.value)} inputProps={{ min: 10, step: 1, 'aria-label': 'Telescope aperture in millimetres' }} />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField fullWidth size="small" label="Reducer / Barlow multiplier (×)" type="number" value={opticalTrain.reducer} onChange={(event) => updateOpticalValue('reducer', event.target.value)} onInput={(event) => updateOpticalValue('reducer', event.currentTarget.value)} inputProps={{ min: 0.2, max: 5, step: 0.01, 'aria-label': 'Reducer or Barlow multiplier. Less than one widens the field, greater than one magnifies.' }} />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField select fullWidth size="small" label="Camera" value={cameraPresetKey} onChange={(event) => applyCameraPreset(event.target.value)} SelectProps={{ native: true }}>
                                {CAMERA_PRESET_KEYS.map((key) => <option key={key} value={key}>{CAMERA_PRESETS[key].label}</option>)}
                              </TextField>
                            </Grid>
                            <Grid item xs={4}>
                              <TextField size="small" label="Sensor W" type="number" value={opticalTrain.sensorWidth} onChange={(event) => updateOpticalValue('sensorWidth', event.target.value)} inputProps={{ min: 1, step: 0.1 }} />
                            </Grid>
                            <Grid item xs={4}>
                              <TextField size="small" label="Sensor H" type="number" value={opticalTrain.sensorHeight} onChange={(event) => updateOpticalValue('sensorHeight', event.target.value)} inputProps={{ min: 1, step: 0.1 }} />
                            </Grid>
                            <Grid item xs={4}>
                              <TextField size="small" label="Pixel µm" type="number" value={opticalTrain.pixelSize} onChange={(event) => updateOpticalValue('pixelSize', event.target.value)} inputProps={{ min: 1, step: 0.01 }} />
                            </Grid>
                          </Grid>
                          <Typography variant="caption" sx={{ color: 'rgba(220,232,255,0.72)', lineHeight: 1.28 }}>
                            Reducer / Barlow updates live: values below 1× widen the FOV; values above 1× zoom in.
                          </Typography>
                          <Box sx={{ position: 'relative', height: 176, borderRadius: 1.6, overflow: 'hidden', border: '1px solid rgba(118,240,255,0.24)', bgcolor: '#030712' }}>
                            <Box sx={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 50% 50%, rgba(118,255,220,0.10), rgba(3,7,18,0.82) 70%), linear-gradient(135deg, rgba(118,240,255,0.10), rgba(245,151,79,0.06))' }} />
                            <Box component="img" src={plannerItemImage(selected)} alt={`${selected.name} target view context`} sx={{ position: 'absolute', inset: '-12%', width: '124%', height: '124%', objectFit: 'cover', opacity: selectedDeepSky ? 0.16 : 0.26, filter: 'blur(10px) saturate(0.9) contrast(0.9)' }} />
                            <Box sx={{ position: 'absolute', inset: '12%', border: '2px solid #76ffdc', boxShadow: '0 0 22px rgba(118,255,220,0.34), inset 0 0 26px rgba(118,255,220,0.10)' }} />
                            <Box sx={{ position: 'absolute', left: '12%', right: '12%', top: '50%', borderTop: '1px solid rgba(118,255,220,0.62)' }} />
                            <Box sx={{ position: 'absolute', top: '12%', bottom: '12%', left: '50%', borderLeft: '1px solid rgba(118,255,220,0.62)' }} />
                            {selectedDeepSky && (
                              <Box sx={{
                                position: 'absolute',
                                left: '50%',
                                top: '50%',
                                width: `${targetView.targetWidthPercent * 0.76}%`,
                                height: `${targetView.targetHeightPercent * 0.76}%`,
                                maxWidth: '180%',
                                maxHeight: '180%',
                                minWidth: 14,
                                minHeight: 12,
                                transform: 'translate(-50%, -50%)',
                                borderRadius: selected.type === 'galaxy' ? '48%' : selected.type === 'planetary' || selected.type === 'globular' ? '50%' : 1,
                                overflow: 'hidden',
                                bgcolor: 'rgba(245,151,79,0.08)',
                                boxShadow: '0 0 22px rgba(245,151,79,0.46)',
                                transition: 'width 0.16s ease, height 0.16s ease'
                              }}>
                                <Box component="img" src={plannerItemImage(selected)} alt={`${selected.name} scaled to ${formatArcMinutes(targetView.fovWidth)} field`} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', opacity: 0.92, filter: 'saturate(1.08) contrast(1.1)' }} />
                                <Box sx={{ position: 'absolute', inset: 0, border: '2px solid #f5974f', borderRadius: 'inherit', boxShadow: 'inset 0 0 18px rgba(245,151,79,0.18)' }} />
                              </Box>
                            )}
                            <Box sx={{ position: 'absolute', left: 10, top: 8, px: 0.8, py: 0.35, borderRadius: 1, bgcolor: 'rgba(3,7,18,0.74)', border: '1px solid rgba(255,255,255,0.10)', color: '#fff', fontSize: '0.68rem', fontWeight: 900 }}>
                              FOV {formatArcMinutes(targetView.fovWidth)} × {formatArcMinutes(targetView.fovHeight)}
                            </Box>
                            <Box sx={{ position: 'absolute', right: 10, top: 8, px: 0.8, py: 0.35, borderRadius: 1, bgcolor: 'rgba(3,7,18,0.74)', border: '1px solid rgba(255,255,255,0.10)', color: '#76ffdc', fontSize: '0.68rem', fontWeight: 900 }}>
                              {targetView.effectiveFocal.toFixed(0)}mm · {Number(safeNumber(opticalTrain.reducer, 1)).toFixed(2)}×
                            </Box>
                            <Box sx={{ position: 'absolute', right: 10, bottom: 8, px: 0.8, py: 0.35, borderRadius: 1, bgcolor: 'rgba(3,7,18,0.74)', border: '1px solid rgba(255,255,255,0.10)', color: '#f5974f', fontSize: '0.68rem', fontWeight: 900 }}>
                              {selectedDeepSky ? selected.size : 'DSO target view'}
                            </Box>
                          </Box>
                          <Grid container spacing={0.65}>
                            {[
                              ['Effective focal', `${Math.round(targetView.effectiveFocal)}mm`],
                              ['F-ratio', `f/${targetView.focalRatio.toFixed(1)}`],
                              ['Pixel scale', `${targetView.pixelScale.toFixed(2)}″/px`],
                              ['Target size', targetView.targetSize.label],
                              ['Frame width', formatArcMinutes(targetView.fovWidth)],
                              ['Frame height', formatArcMinutes(targetView.fovHeight)]
                            ].map(([label, value]) => (
                              <Grid item xs={6} key={label}>
                                <Box sx={{ p: 0.65, borderRadius: 1.1, bgcolor: 'rgba(255,255,255,0.045)', border: '1px solid rgba(255,255,255,0.07)' }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</Typography>
                                  <Typography sx={{ fontWeight: 900, fontSize: '0.76rem', lineHeight: 1.2 }}>{value}</Typography>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                          <Typography variant="caption" sx={{ color: '#dce8ff', lineHeight: 1.35 }}>
                            {targetView.framing} The cyan rectangle is the camera frame; the amber footprint is the catalogue apparent size scaled against that field.
                          </Typography>
                        </Stack>
                      </Collapse>
                    </Box>
                    <Button fullWidth variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={() => addToPlanner()} sx={{ color: '#160920', fontWeight: 900 }}>
                      {selectedIsSolar ? `Add ${selected.name} to night planning guide` : 'Add to night planning guide'}
                    </Button>
                  </Stack>
                </Paper>
              ) : null}

              <Paper sx={{ p: 1.15, background: 'rgba(15,17,28,0.92)', border: '1px solid rgba(255,255,255,0.10)' }}>
                <Stack spacing={0.9} sx={{ '& .MuiInputBase-root': { minHeight: 36 }, '& .MuiButton-root': { minHeight: 31, py: 0.35, fontSize: '0.72rem' }, '& .MuiSlider-root': { py: 0.4 }, '& .MuiToggleButton-root': { py: 0.45, fontSize: '0.68rem' } }}>
                  <Stack direction="row" spacing={0.75} alignItems="center"><FilterAltIcon sx={{ color: 'primary.light', fontSize: 18 }} /><Typography sx={{ fontWeight: 900, fontSize: '0.96rem' }}>Target filters</Typography></Stack>
                  <TextField size="small" placeholder="Search M31, Orion, Cygnus…" value={query} onChange={(event) => { pauseMotionForFilterChange(); setQuery(event.target.value); }} InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }} />
                  <Box sx={{ p: 0.8, borderRadius: 1.4, border: '1px solid rgba(118,240,255,0.22)', bgcolor: 'rgba(118,240,255,0.04)' }}>
                    <Stack spacing={0.7}>
                      <Stack direction="row" spacing={0.65} alignItems="center">
                        <CalendarMonthIcon sx={{ color: '#76ffdc', fontSize: 16 }} />
                        <Typography variant="caption" sx={{ color: '#76ffdc', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Observing date</Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.65} alignItems="stretch">
                        <Box sx={{ flex: 1, position: 'relative', minWidth: 0 }}>
                          <TextField
                            inputRef={dateInputRef}
                            size="small"
                            value={observingDateDisplay}
                            onClick={() => setCalendarOpen((value) => !value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setCalendarOpen((value) => !value);
                              }
                              if (event.key === 'Escape') setCalendarOpen(false);
                            }}
                            inputProps={{ 'aria-label': 'Observing date', readOnly: true, role: 'button', 'aria-expanded': calendarOpen }}
                            sx={{
                              width: '100%',
                              '& .MuiInputBase-root': {
                                minHeight: 42,
                                pr: 4.4,
                                borderRadius: 1.4,
                                color: '#f8fbff',
                                fontWeight: 850,
                                letterSpacing: '0.01em',
                                cursor: 'pointer',
                                background: 'linear-gradient(180deg, rgba(5,10,24,0.96), rgba(11,17,34,0.92))',
                                boxShadow: 'inset 0 0 0 1px rgba(118,240,255,0.12), 0 0 24px rgba(118,240,255,0.05)'
                              },
                              '& .MuiOutlinedInput-notchedOutline': { borderColor: calendarOpen ? '#f5974f' : 'rgba(245,151,79,0.50)' },
                              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(118,240,255,0.62)' },
                              '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#f5974f', borderWidth: 1.5 },
                              '& input': { py: 1.05, cursor: 'pointer' }
                            }}
                          />
                          <IconButton
                            size="small"
                            aria-label="Open observing date calendar"
                            onClick={() => setCalendarOpen((value) => !value)}
                            sx={{
                              position: 'absolute',
                              right: 5,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              width: 31,
                              height: 31,
                              borderRadius: 1.15,
                              color: '#06111b',
                              bgcolor: '#76ffdc',
                              border: '1px solid rgba(255,255,255,0.18)',
                              boxShadow: '0 0 18px rgba(118,255,220,0.34)',
                              '&:hover': { bgcolor: '#f5974f', color: '#120714', boxShadow: '0 0 22px rgba(245,151,79,0.42)' }
                            }}
                          >
                            <CalendarMonthIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                          {calendarOpen && (
                            <Paper sx={{
                              position: 'absolute',
                              zIndex: 40,
                              top: 'calc(100% + 8px)',
                              left: 0,
                              width: { xs: 268, sm: 292 },
                              maxWidth: 'calc(100vw - 34px)',
                              p: 1,
                              borderRadius: 1.8,
                              background: 'linear-gradient(180deg, rgba(9,14,30,0.98), rgba(4,7,17,0.99))',
                              border: '1px solid rgba(245,151,79,0.42)',
                              boxShadow: '0 24px 70px rgba(0,0,0,0.55), 0 0 32px rgba(118,240,255,0.10)'
                            }}>
                              <Stack spacing={0.75}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                  <IconButton size="small" aria-label="Previous month" onClick={() => setCalendarMonthOffset(-1)} sx={{ color: '#76ffdc', border: '1px solid rgba(118,240,255,0.18)' }}>‹</IconButton>
                                  <Typography sx={{ color: '#f8fbff', fontWeight: 900, fontSize: '0.88rem', letterSpacing: '0.01em' }}>{calendarMonthLabel}</Typography>
                                  <IconButton size="small" aria-label="Next month" onClick={() => setCalendarMonthOffset(1)} sx={{ color: '#76ffdc', border: '1px solid rgba(118,240,255,0.18)' }}>›</IconButton>
                                </Stack>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.35 }}>
                                  {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((day) => (
                                    <Typography key={day} variant="caption" sx={{ textAlign: 'center', color: '#76ffdc', fontWeight: 900, fontSize: '0.64rem' }}>{day}</Typography>
                                  ))}
                                  {calendarDays.map((day) => (
                                    <Button
                                      key={day.iso}
                                      size="small"
                                      onClick={() => selectObservingDate(day.iso)}
                                      sx={{
                                        minWidth: 0,
                                        height: 30,
                                        px: 0,
                                        borderRadius: 1.1,
                                        color: day.selected ? '#120714' : day.currentMonth ? '#f8fbff' : 'rgba(220,232,255,0.42)',
                                        bgcolor: day.selected ? '#f5974f' : day.today ? 'rgba(118,255,220,0.15)' : 'rgba(255,255,255,0.025)',
                                        border: day.today ? '1px solid rgba(118,255,220,0.55)' : '1px solid rgba(255,255,255,0.06)',
                                        fontWeight: day.selected || day.today ? 900 : 750,
                                        '&:hover': { bgcolor: day.selected ? '#f5974f' : 'rgba(118,240,255,0.16)', color: '#fff' }
                                      }}
                                    >{day.day}</Button>
                                  ))}
                                </Box>
                                <Stack direction="row" spacing={0.6} justifyContent="space-between">
                                  <Button size="small" variant="text" onClick={() => setCalendarOpen(false)}>Close</Button>
                                  <Button size="small" variant="outlined" onClick={() => selectObservingDate(todayIsoDate())}>Today</Button>
                                </Stack>
                              </Stack>
                            </Paper>
                          )}
                        </Box>
                        <Button
                          variant="outlined"
                          onClick={() => selectObservingDate(todayIsoDate())}
                          sx={{ minWidth: 58, px: 1, borderColor: 'rgba(245,151,79,0.54)', bgcolor: 'rgba(245,151,79,0.08)' }}
                        >Today</Button>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.25 }}>
                        Planning for {observingDateLabel}; target visibility, Moon phase and the {observingWindowLabel} timeline use this date.
                      </Typography>
                    </Stack>
                  </Box>
                  <Box sx={{ p: 0.8, borderRadius: 1.4, border: '1px solid rgba(245,151,79,0.24)', bgcolor: 'rgba(245,151,79,0.045)' }}>
                    <Stack spacing={0.7}>
                      <Stack direction="row" spacing={0.65} alignItems="center">
                        <LocationOnIcon sx={{ color: 'primary.light', fontSize: 16 }} />
                        <Typography variant="caption" sx={{ color: 'primary.light', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Trip / viewer location</Typography>
                      </Stack>
                      <Stack direction={{ xs: 'column', sm: 'row', lg: 'column', xl: 'row' }} spacing={0.6}>
                        <TextField
                          size="small"
                          placeholder="Town, city or postcode…"
                          value={locationQuery}
                          onChange={(event) => setLocationQuery(event.target.value)}
                          onKeyDown={(event) => { if (event.key === 'Enter') searchPlanningLocations(); }}
                          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 0.75, color: 'text.secondary', fontSize: 17 }} /> }}
                          sx={{ flex: 1 }}
                        />
                        <Button variant="outlined" onClick={searchPlanningLocations} disabled={locationSearching} startIcon={locationSearching ? <CircularProgress size={13} /> : <SearchIcon />} sx={{ minWidth: { xl: 86 } }}>Search</Button>
                      </Stack>
                      <Stack direction="row" spacing={0.6}>
                        <Button variant="outlined" startIcon={<MyLocationIcon />} onClick={useViewerLocation} sx={{ flex: 1, justifyContent: 'flex-start' }}>Use device</Button>
                        <Button variant="text" onClick={resetPlanningLocation} sx={{ minWidth: 64 }}>Default</Button>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.25 }}>
                        Active: {viewerLocation.label} ({viewerLocation.latitude.toFixed(2)}°, {viewerLocation.longitude.toFixed(2)}°)
                      </Typography>
                      {locationResults.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.45 }}>Other matches</Typography>
                          <Stack direction="row" spacing={0.45} sx={{ flexWrap: 'wrap', gap: 0.45 }}>
                            {locationResults.map((result) => (
                              <Chip
                                key={`${result.id || result.label}-${result.latitude}-${result.longitude}`}
                                size="small"
                                icon={<LocationOnIcon />}
                                label={result.label || displayNameForLocation(result)}
                                clickable
                                onClick={() => applyPlanningLocation(result)}
                                sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Hemisphere mode: {hemisphereMode === 'auto' ? `Auto → ${activeHemisphereLabel}` : activeHemisphereLabel}</Typography>
                    <ToggleButtonGroup value={hemisphereMode} exclusive onChange={(event, value) => { if (value) setHemisphereMode(value); }} size="small" fullWidth sx={{ mt: 0.35 }}>
                      <ToggleButton value="auto">Auto</ToggleButton>
                      <ToggleButton value="north">North</ToggleButton>
                      <ToggleButton value="south">South</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.25 }}>Targets must clear the altitude line during {observingWindowLabel}; below-horizon targets are hidden until they rise. Moon, planets, ISS and selected real comet use the observing date, chart time and viewer location.</Typography>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Optional sky overlays</Typography>
                    <ToggleButtonGroup
                      value={solarFilters}
                      onChange={(event, value) => { pauseMotionForFilterChange(); setSolarFilters(value); }}
                      size="small"
                      fullWidth
                      sx={{ mt: 0.35, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.4, '& .MuiToggleButtonGroup-grouped': { border: '1px solid rgba(255,255,255,0.12) !important', borderRadius: '9px !important', m: '0 !important' }, '& .MuiToggleButton-root': { py: 0.45, fontSize: '0.68rem' } }}
                    >
                      <ToggleButton value="moon">Moon</ToggleButton>
                      <ToggleButton value="planet">Planets</ToggleButton>
                      <ToggleButton value="iss">ISS</ToggleButton>
                      <ToggleButton value="custom">Comet</ToggleButton>
                    </ToggleButtonGroup>
                    <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.55 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>Comet</Typography>
                      <TextField
                        select
                        size="small"
                        value={selectedCometKey}
                        onChange={(event) => { pauseMotionForFilterChange(); setSelectedCometKey(event.target.value); setSelectedId(null); }}
                        SelectProps={{ native: true }}
                        sx={{ flex: 1, '& .MuiInputBase-root': { minHeight: 30, fontSize: '0.72rem' } }}
                      >
                        {Object.entries(REAL_COMETS).map(([key, comet]) => <option key={key} value={key}>{comet.name}</option>)}
                      </TextField>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.4, lineHeight: 1.25 }}>ISS and Comet start off. Turn Comet on after choosing a real comet to inspect its selected-night trajectory.</Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">Hemisphere observable band</Typography>
                    <Button size="small" variant={onlyObservable ? 'contained' : 'outlined'} onClick={() => setOnlyObservable((value) => !value)} sx={{ minWidth: 52 }}>{onlyObservable ? 'On' : 'Off'}</Button>
                  </Stack>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Minimum night altitude: {minAltitude}°</Typography>
                    <Slider value={minAltitude} min={0} max={45} step={1} onChange={(event, value) => setMinAltitude(value)} />
                  </Box>
                  {locationMessage && <Typography variant="caption" color="text.secondary">{locationMessage}</Typography>}
                  <ToggleButtonGroup value={filters} onChange={(event, value) => { if (value.length) { pauseMotionForFilterChange(); setFilters(value); } }} size="small" fullWidth sx={{ '& .MuiToggleButton-root': { px: 0.55 } }}>
                    {Object.entries(OBJECT_TYPES).map(([key, type]) => (
                      <ToggleButton key={key} value={key} sx={{ px: 0.8, color: type.color, '&.Mui-selected': { color: type.color, bgcolor: `${type.glow}` } }}>{type.label.split(' ')[0]}</ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Catalogue filters</Typography>
                    <ToggleButtonGroup
                      value={catalogueFilters}
                      onChange={(event, value) => { pauseMotionForFilterChange(); setCatalogueFilters(value); }}
                      size="small"
                      sx={{ mt: 0.35, display: 'flex', flexWrap: 'wrap', gap: 0.4, '& .MuiToggleButtonGroup-grouped': { border: '1px solid rgba(255,255,255,0.12) !important', borderRadius: '9px !important', m: 0 } }}
                    >
                      {Object.entries(CATALOGUE_FILTERS).map(([key, catalogue]) => (
                        <ToggleButton key={key} value={key} sx={{ px: 0.65, py: 0.35, fontSize: '0.64rem', color: '#dce8ff', '&.Mui-selected': { color: '#050711', bgcolor: key === 'messier' || key === 'caldwell' ? '#f5974f' : '#72f0ff' } }}>{catalogue.short}</ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.4, lineHeight: 1.25 }}>
                      Messier, NGC, IC, Sh2, Barnard, LDN/LBN, Caldwell, Arp, Abell, PK.
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Map zoom</Typography>
                    <Slider value={zoom} min={0.72} max={4.5} step={0.01} onChange={(event, value) => { setZoom(value); setAutoRotate(false); }} />
                  </Box>
                </Stack>
              </Paper>

            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
